import { PageTitle } from '~/_shared/constants/routes';
import { EmailErrorCode } from '~/_shared/types/responseErrors/_shared/emailErrorCodes.types';
import { FileUploadErrorCodes } from '~/_shared/types/responseErrors/_shared/fileUploadErrorCodes.types';
import { GeneralErrorCode } from '~/_shared/types/responseErrors/_shared/generalErrorCodes.types';
import { MFAErrorCode } from '~/_shared/types/responseErrors/_shared/MFAErrorCodes.types';
import { PasswordErrorCode } from '~/_shared/types/responseErrors/_shared/passwordErrorCodes.types';
import { SpreadsheetImportErrorCodes } from '~/_shared/types/responseErrors/_shared/spreadsheetImportErrorCodes.types';
import { ValueValidationErrorCode } from '~/_shared/types/responseErrors/_shared/valueValidationErrorCodes.types';
import { RefreshTokenErrorCodes } from '~/authorization/authContext/auth.utils';
import { CustomizationLevel } from '~/customizeMarkerPopup/customizeLabelAndMarker.types';
import { RestoreOption } from '~/customizeMarkerPopup/restoreDefault/restoreDefaultMarkerCustomizations.enums';
import { DrawingTool } from '~/drawingTool/drawingTool.enums';
import { CreateMapErrorCode } from '~/map/createNew/createMapErrorCodes.types';
import { MapSearchFilterBy } from '~/map/filter/mapSearchFilterBy.enum';
import {
  LottieActiveMarkerIndicators, SvgActiveMarkerIndicators,
} from '~/map/map/mapOverlays/activeMarkerIndicator/activeMarkerIndicator.types';
import { DataType } from '~/store/spreadsheetData/spreadsheetData.state';
import { UserLoginErrorSpecialCode } from '~/store/userData/repository/userLogin.types';
import { type TranslationsStrings } from '../translations.type';

const translations: TranslationsStrings = {
  // Start of translation lines
  ' or drop an image here.': ' or drop an image here.',
  ' or drop files here.': ' or drop files here.',
  ' requires entire area to be placed inside of the lasso to be selected.': ' requires entire area to be placed inside of the lasso to be selected.',
  ' requires only part of the area to be placed inside of the lasso to be selected.': ' requires only part of the area to be placed inside of the lasso to be selected.',
  ' the outline and click the remove button to delete the vertex.': ' the outline and click the remove button to delete the vertex.',
  ' the outline vertex to reshape your polygon.': ' the outline vertex to reshape your polygon.',
  '(optional)': '(optional)',
  '* Filter On/Off': '* Filter On/Off',
  '2 Factor Authentication Code': '2 Factor Authentication Code',
  '2 Factor Authentication was successfully disabled': '2 Factor Authentication was successfully disabled cs',
  '2 Factor Authorization via Email was successfully activated': '2 Factor Authorization via Email was successfully activated',
  '2 Factor Authorization via Google Authenticator was successfully activated': '2 Factor Authorization via Google Authenticator was successfully activated',
  '<0>Add File</0> or drop files here.': '<0>Add File</0> or drop files here.',
  '<0>Delete Layered Map:</0> By completing this action, the base map will be deleted. However, please be aware that the layered map in which the base map was used will also be deleted as a result. If you have any questions or need assistance, feel free to contact our support team for help.': '<0>Delete Layered Map:</0> By completing this action, the base map will be deleted. However, please be aware that the layered map in which the base map was used will also be deleted as a result. If you have any questions or need assistance, feel free to contact our support team for help.',
  '<0>Delete layer:</0> will remove the current base map from the layered map. This means the markers associated with this base map will not be shown in the layered map anymore.': '<0>Delete layer:</0> will remove the current base map from the layered map. This means the markers associated with this base map will not be shown in the layered map anymore.',
  '<0>Full</0> requires entire area to be placed inside of the lasso to be selected.': '<0>Full</0> requires entire area to be placed inside of the lasso to be selected.',
  '<0>Make Map Disconnected:</0> will copy the spreadsheet data from all the single base maps composing the layered map into one new spreadsheet.  From now on, the layered map  look the same but the source of the markers are different - the data will not get updated based on the original source.': '<0>Make Map Disconnected:</0> will copy the spreadsheet data from all the single base maps composing the layered map into one new spreadsheet.  From now on, the layered map  look the same but the source of the markers are different - the data will not get updated based on the original source.',
  '<0>Partial</0> requires only part of the area to be placed inside of the lasso to be selected.': '<0>Partial</0> requires only part of the area to be placed inside of the lasso to be selected.',
  '<0>Refresh the page</0> if you want to return to online mode.': '<0>Refresh the page</0> if you want to return to online mode.',
  '<0>Upgrade here</0> to enable export': '<0>Upgrade here</0> to enable export',
  '<0>You</0> have changed the following recently. Consider checking or closing other browser tabs.': '<0>You</0> have changed the following recently. Consider checking or closing other browser tabs.',
  'A 2FA code was sent to your email address': 'A 2FA code was sent to your email address.',
  'A <1>Layered Map</1> is composed of several base maps - it consists of the union of the data of the base maps.': 'A <1>Layered Map</1> is composed of several base maps - it consists of the union of the data of the base maps.',
  'A Migration Is Currently Underway': 'A Migration Is Currently Underway',
  'Account Settings': 'Account Settings',
  'Account settings updated': 'Account settings updated',
  'Activating 2 Factor Authentication via Email has failed': 'Activating 2 Factor Authentication via Email has failed',
  'Activating 2 Factor Authentication via Google Authenticator has failed': 'Activating 2 Factor Authentication via Google Authenticator has failed',
  'Active Indicators': 'Active Indicators',
  'Active Marker Indicator Style': 'Active Marker Indicator Style',
  'Active Marker Indicators': 'Active Marker Indicators',
  'Add Additional Location': 'Add Additional Location',
  'Add Another Identifier': 'Add Another Identifier',
  'Add Another Location': 'Add Another Location',
  'Add Description': 'Add Description',
  'Add Drive Time Polygon': 'Add Drive Time Polygon',
  'Add Elements': 'Add Elements',
  'Add File': 'Add File',
  'Add Grouping Column': 'Add Grouping Column',
  'Add Heat Map': 'Add Heat Map',
  'Add Marker Label': 'Add Marker Label',
  'Add Marker': 'Add Marker',
  'Add New "{{columnName}}" Column': 'Add New "{{columnName}}" Column',
  'Add New Column': 'Add a New Column',
  'Add New Row': 'Add New Row',
  'Add New': 'Add New',
  'Add Proximity Radius': 'Add Proximity Radius',
  'Add Radius': 'Add Radius',
  'Add Territory Group': 'Add Territory Group',
  'Add Territory': 'Add Territory',
  'Add User': 'Add User',
  'Add Users': 'Add Users',
  'Add a Map Marker to your Map': 'Add a Map Marker to your Map',
  'Add a New Column to Data Sheet': 'Add a New Column to Data Sheet',
  'Add a New Row to Data Sheet': 'Add a New Row to Data Sheet',
  'Add a New Team Member': 'Add a New Team Member',
  'Add another directions instance': 'Add another directions instance',
  'Add base maps': 'Add base maps',
  'Add boundaries to existing fill group': 'Add boundaries to existing fill group',
  'Add to Boundary Details': 'Add to Boundary Details',
  'Add to Proximity Details': 'Add to Proximity Details',
  'Add to Route': 'Add to Route',
  'Add to Territory Details': 'Add to Territory Details',
  'Add to existing Group': 'Add to existing Group',
  'Add {{drawingToolName}}': 'Add {{drawingToolName}}',
  'Add/Remove Maps to Your Layered Map': 'Add/Remove Maps to Your Layered Map',
  'Additional Actions': 'Additional Actions',
  'Additional Column': 'Additional Column',
  'Additional Message - optional': 'Additional Message - optional',
  'Address Column as Unique Identifier': 'Address Column as Unique Identifier',
  'Address Column': 'Address Column',
  'Address Line 2': 'Address Line 2',
  'Advanced Add / Edit {{boundaryType}}': 'Advanced Add / Edit {{boundaryType}}',
  'Advanced Settings': 'Advanced Settings',
  'AfterMigrationMapPrepareModal.Header': 'Map is being prepared',
  'AfterMigrationMapPrepareModal.Headline.Finished': 'Your Map is now updated and ready for some supercharged mapping.',
  'AfterMigrationMapPrepareModal.Headline.InProgress': 'Your Map is being updated so that it’s compatible with the new version of Maptive.',
  'AfterMigrationMapPrepareModal.Subheading': 'The process will just take a few moments.',
  'Align Bottom': 'Align Bottom',
  'Align Center': 'Align Center',
  'Align each source’s fields into one output column.': 'Align each source’s fields into one output column.',
  'All Filtered Locations': 'All Filtered Locations',
  'All Layered Maps': 'All Layered Maps',
  'All Maps A to Z': 'All Maps A to Z',
  'All Maps Z to A': 'All Maps Z to A',
  'All Markers in Data': 'All Markers in Data',
  'All Markers outside of Results': 'All Markers outside of Results',
  'All Radii in Group': 'All Radii in Group',
  'All Tools and Features': 'All Tools and Features',
  'All data replaced': 'All data replaced',
  'Allow Satellite View': 'Allow Satellite View',
  'Allow Street View': 'Allow Street View',
  'Allow in Export on Map': 'Allow in Export on Map',
  'Allow zoom levels': 'Allow zoom levels',
  'Already a user?': 'Already a user?',
  'An error occurred while adding a new column': 'An error occurred while adding a new column',
  'An error occurred while adding a new row': 'An error occurred while adding a new row',
  'An error occurred while creating the boundary': 'An error occurred while creating the boundary',
  'An error occurred while exporting the map data': 'An error occurred while exporting the map data',
  'An error occurred while getting the group column data. Please try again later or select a different group column.': 'An error occurred while getting the group column data. Please try again later or select a different group column.',
  'An error occurred while getting the location': 'An error occurred while getting the location',
  'An error occurred while migrating your maps': 'An error occurred while migrating your maps',
  'An error occurred while removing the data': 'An error occurred while removing the data',
  'An error occurred while updating the data': 'An error occurred while updating the data',
  'An unknown error has occurred.': 'An unknown error has occurred.',
  'Anchor Point Importance': 'Anchor Point Importance',
  'Any change made to the base map data will only be reflected on the layered map data after you approve the update. Layered map data will remain in its previous state if base map data updates are NOT approved by you.': 'Any change made to the base map data will only be reflected on the layered map data after you approve the update. Layered map data will remain in its previous state if base map data updates are NOT approved by you.',
  'Any changes done to the base map spreadsheet data will be automatically reflected in the layered map spreadsheet data.': 'Any changes done to the base map spreadsheet data will be automatically reflected in the layered map spreadsheet data.',
  'Anyone with the link can view.': 'Anyone with the link can view.',
  'Api Id': 'Api Id',
  'Apply changes': 'Apply changes',
  'Apply to': 'Apply to',
  'Are you sure you want to cancel your license?': 'Are you sure you want to cancel your license?',
  'Are you sure you want to decline the invitation?': 'Are you sure you want to decline the invitation?',
  'Are you sure you want to delete territory group? It is currently used on {{count}} maps.': 'Are you sure you want to delete territory group? It is currently used on {{count}} maps.',
  'Are you sure you want to delete territory group? It is currently used on {{count}} maps._one': 'Are you sure you want to delete territory group? It is currently used on {{count}} map.',
  'Are you sure you want to delete this territory?': 'Are you sure you want to delete this territory?',
  'Are you sure you want to delete your account along with all data associated with it, including your license?': 'Are you sure you want to delete your account along with all data associated with it, including your license?',
  'Are you sure you want to proceed?': 'Are you sure you want to proceed?',
  'Arrow Element': 'Arrow Element',
  'Ask me later': 'Ask me later',
  'Assign As Primary': 'Assign As Primary',
  'Assign specific map roles to your team members.': 'Assign specific map roles to your team members.',
  'At least one of the columns: Address, City, State, Zip, Country, or both Latitude and Longitude columns must be selected to categorize the data.': 'At least one of the columns: Address, City, State, Zip, Country, or both Latitude and Longitude columns must be selected to categorize the data.',
  'At this map zoom level, some boundaries are not visible on the map. To see your boundaries you must zoom in a few clicks.': 'At this map zoom level, some boundaries are not visible on the map. To see your boundaries you must zoom in a few clicks.',
  'Auto Territory Builder - trial': 'Auto Territory Builder - trial',
  'Auto Territory Builder requires a subscription which your account does not have active. You can try this tool with trial limitations:': 'Auto Territory Builder requires a subscription which your account does not have active. You can try this tool with trial limitations:',
  'Auto Territory Builder': 'Auto Territory Builder',
  'Back to App': 'Back to App',
  'Back to Login': 'Back to Login',
  'Balancing Territories': 'Balancing Territories',
  'Base Map Themes': 'Base Map Themes',
  'Base Map': 'Base Map',
  'Base Map(s) Updated': 'Base Map(s) Updated',
  'Batch Edit Locations': 'Batch Edit Locations',
  'Batch Edit': 'Batch Edit',
  'Blue Bubble Markers': 'Blue Bubble Markers',
  'Border Line Color': 'Border Line Color',
  'Border Line Opacity': 'Border Line Opacity',
  'Border Line Stroke': 'Border Line Stroke',
  'Border line Color & Stroke': 'Border line Color & Stroke',
  'Boundaries Out Of Sync': 'Boundaries Out Of Sync',
  'Boundaries have been recently changed by {{userName}}. Please refresh your data in order to be able to make changes.': 'Boundaries have been recently changed by {{userName}}. Please refresh your data in order to be able to make changes.',
  'Boundaries requiring zoom': 'Boundaries requiring zoom',
  'Boundary / Territory': 'Boundary / Territory',
  'Boundary Create Error': 'Boundary Create Error',
  'Boundary Formatting': 'Boundary Formatting',
  'Boundary Group is already loaded': 'Boundary Group is already loaded',
  'Boundary Legend': 'Boundary Legend',
  'Boundary Remove Error': 'Boundary Remove Error',
  'Boundary Selection Tool': 'Boundary Selection Tool',
  'Boundary Settings': 'Boundary Settings',
  'Boundary Territory Legend': 'Boundary Territory Legend',
  'Boundary Update Error': 'Boundary Update Error',
  'Boundary was already added to the map': 'Boundary was already added to the map',
  'Boundary was already added': 'Boundary was already added',
  'Boundary was already removed from the map': 'Boundary was already removed from the map',
  'Boundary was already removed': 'Boundary was already removed',
  'Boundary: {{boundaryName}}': 'Boundary: {{boundaryName}}',
  'BoundaryEdit.SubmitAction.Add': 'Add the {{method}} area to an existing territory',
  'BoundaryEdit.SubmitAction.Reassign': 'Reassign the {{method}} area between territories',
  'BoundaryEdit.SubmitAction.Remove': 'Remove the {{method}} area from an existing territory',
  'Break up this column matching': 'Break up this column matching',
  'Bring to front': 'Bring to front',
  'Bring to the middle': 'Bring to the middle',
  'By accepting the invitation from the company {{company}}, all your existing maps will be transferred under this company. Do you wish to accept the invitation and continue?': 'By accepting the invitation from the company {{company}}, all your existing maps will be transferred under this company. Do you wish to accept the invitation and continue?',
  'By accepting the invitation, all your existing maps will be transferred under this company. Do you wish to accept the invitation and continue?': 'By accepting the invitation, all your existing maps will be transferred under this company. Do you wish to accept the invitation and continue?',
  'By clicking the "Create Account" button I agree to the <1>Terms of Service</1> & <3>Privacy Policy</3>': 'By clicking the "Create Account" button I agree to the <1>Terms of Service</1> & <3>Privacy Policy</3>',
  'Calculating Statistics': 'Calculating Statistics',
  'Callout Element': 'Callout Element',
  'Cancel Lasso': 'Cancel Lasso',
  'Cancel Now / Delete my Data': 'Cancel Now / Delete my Data',
  'Cancel Subscription': 'Cancel Subscription',
  'Cannot change Security Status for this Map View when it\'s Main Map is Private.': 'Cannot change Security Status for this Map View when its Main Map is Private.',
  'Cannot use selected fill option before geocoding is completed': 'Cannot use selected fill option before geocoding is completed',
  'Categorize Data': 'Categorize Data',
  'Change Email': 'Change Email',
  'Change Password': 'Change Password',
  'Changes Pending': 'Changes Pending',
  'Changes done to the base map data, after the layered map is created, will never update the layered map data or be reflected on the layered map.': 'Changes done to the base map data, after the layered map is created, will never update the layered map data or be reflected on the layered map.',
  'Check if your address is complete and correct, or please try with a different address.': 'Check if your address is complete and correct, or please try with a different address.',
  'Check the first {{maxLocations}} locations': 'Check the first {{maxLocations}} locations',
  'Check whether the selected column(s) form a unique identifier first.': 'Check whether the selected column(s) form a unique identifier first.',
  'Check your email to log in.': 'Check your email to log in.',
  'Choose Selection Method': 'Choose Selection Method',
  'Choose a Column for Marker Label': 'Choose a Column for Marker Label',
  'Choose a Two-Factor authentication method': 'Choose a Two-Factor authentication method',
  'Choose a column': 'Choose a column',
  'Choose a different email address': 'Choose a different email address',
  'Choose audience': 'Choose audience',
  'Choose the boundary / territory groups you want to export': 'Choose the boundary / territory groups you want to export',
  'Choose the directions you want to export': 'Choose the directions you want to export',
  'Choose the proximities you want to export': 'Choose the proximities you want to export',
  'Circle Element': 'Circle Element',
  'Clear Form': 'Clear Form',
  'Click <1>here</1> if you wish to logout.': 'Click <1>here</1> if you wish to logout.',
  'Click again to stop adding areas to your selection.': 'Click again to stop adding areas to your selection.',
  'Click here': 'Click here',
  'Click on the measurement option you want and then click on the map to begin your measurement from the starting location.': 'Click on the measurement option you want and then click on the map to begin your measurement from the starting location.',
  'Click once to start adding areas to your selection.': 'Click once to start adding areas to your selection.',
  'Click the <1>Save</1> button after your drawing is finished.': 'Click the <1>Save</1> button after your drawing is finished.',
  'Click the <1>Save</1> button after your selection is finished.': 'Click the <1>Save</1> button after your selection is finished.',
  'Click the <1>Start</1> button to begin.': 'Click the <1>Start</1> button to begin.',
  'Click the areas you wish to add to your selection.': 'Click the areas you wish to add to your selection.',
  'Click the first vertex or double click the map to complete the loop.': 'Click the first vertex or double click the map to complete the loop.',
  'Click the map again to add additional vertices to the polygon outline.': 'Click the map again to add additional vertices to the polygon outline.',
  'Click the map to start drawing your polygon.': 'Click the map to start drawing your polygon.',
  'Click the markers you wish to add to your selection.': 'Click the markers you wish to add to your selection.',
  'Close Page': 'Close Page',
  'Close Street View': 'Close Street View',
  'Close Tab': 'Close Tab',
  'Cluster Dense Markers': 'Cluster Dense Markers',
  'Cluster w/Pie Charts': 'Cluster w/Pie Charts',
  'Clustering of Markers was Enabled': 'Clustering of Markers was Enabled',
  'Collapse All': 'Collapse All',
  'Collapse Tool': 'Collapse Tool',
  'Collapse snapshots': 'Collapse snapshots',
  'Column Name': 'Column Name',
  'Column labels / content spacing': 'Column labels / content spacing',
  'Column {{number}}': 'Column {{number}}',
  'Combine Groups': 'Combine Groups',
  'Company Admin': 'Account Admin',
  'Company Name': 'Company Name',
  'Company Owner': 'Account Owner',
  'Company name must not be empty': 'Company name must not be empty',
  'Confirm Email': 'Confirm Email',
  'Confirm New Password': 'CONFIRM NEW PASSWORD',
  'Confirm Password': 'Confirm Password',
  'Congratulations!': 'Congratulations!',
  'Connectivity Settings': 'Connectivity Settings',
  'Contact Info': 'Contact Info',
  'Contact Maptive Support': 'Contact Maptive Support',
  'Contact Owner': 'Contact Owner',
  'Contact Support': 'Contact Support',
  'Continue Drawing': 'Continue Drawing',
  'Continue Offline': 'Continue Offline',
  'Continue With Boundaries': 'Continue With Boundaries',
  'Continue to Login': 'Continue to Login',
  'Convert Groups to Territories': 'Convert Groups to Territories',
  'Copied to Clipboard!': 'Copied to Clipboard!',
  'Copy Address to Clipboard': 'Copy Address to Clipboard',
  'Copy Apple Maps Link': 'Copy Apple Maps Link',
  'Copy Coordinates to Clipboard': 'Copy Coordinates to Clipboard',
  'Copy Data to Clipboard': 'Copy Data to Clipboard',
  'Copy Google Maps Link': 'Copy Google Maps Link',
  'Copy Map Link': 'Copy Map Link',
  'Copy Map': 'Copy Map',
  'Copy Territory Group': 'Copy Territory Group',
  'Copy to Clipboard': 'Copy to Clipboard',
  'Create Account': 'Create Account',
  'Create Fill Group': 'Create Fill Group',
  'Create Filters': 'Create Filters',
  'Create Map View': 'Create Map View',
  'Create My First Map': 'Create My First Map',
  'Create New Layered Map': 'Create New Layered Map',
  'Create New Map': 'Create New Map',
  'Create New Territory Group': 'Create New Territory Group',
  'Create New Territory': 'Create New Territory',
  'Create Territories Manually': 'Create Territories Manually',
  'Create Territories': 'Create Territories',
  'Create Territory Group': 'Create Territory Group',
  'Create Your Filters': 'Create Your Filters',
  'Create a Map That Identifies Sales Rep Locations': 'Create a Map That Identifies Sales Rep Locations',
  'Create a New Map with Google Sheets.': 'Create a New Map with Google Sheets.',
  'Create a new Map View': 'Create a new Map View',
  'Create new fill group': 'Create new fill group',
  'Create your own map link using the form below:': 'Create your own map link using the form below:',
  'Created using Maptive': 'Created using Maptive',
  'Current Active Search Queries': 'Current Active Search Queries',
  'Current Email Address': 'Current Email Address',
  'Current License Period': 'Current License Period',
  'Current Password': 'Current Password',
  'Currently this tool supports a maximum of {{limit}} hours.': 'Currently this tool supports a maximum of {{limit}} hours.',
  'Currently this tool supports maximum of {{limit}} locations. Please remove {{overLimit}} locations to continue.': 'Currently this tool supports maximum of {{limit}} locations. Please remove {{overLimit}} locations to continue.',
  'Custom Color': 'Custom Color',
  'Customize Active Marker Indicator': 'Customize Active Marker Indicator',
  'Customize Boundary Metrics': 'Customize Boundary Metrics',
  'Customize Fill by data': 'Customize Fill by data',
  'Customize Info Display': 'Customize Info Display',
  'Customize Labels': 'Customize Labels',
  'Customize Map Link (URL)': 'Customize Map Link (URL)',
  'Customize Map Markers': 'Customize Map Markers',
  'Customize Map Url': 'Customize Map Url',
  'Customize Marker': 'Customize Marker',
  'Customize Metrics': 'Customize Metrics',
  'Customize Proximity Metrics': 'Customize Proximity Metrics',
  'Customize Settings For': 'Customize Settings For:',
  'Customize Territory Metrics': 'Customize Territory Metrics',
  'Customize What?': 'Customize What?',
  'CustomizeLabelAndMarker-Label': 'Label Inside Marker',
  'CustomizeLabelAndMarker-Marker': 'Marker Textures',
  'DIRECTIONS BEGIN AT YOUR LOCATION': 'DIRECTIONS BEGIN AT YOUR LOCATION',
  'Data Action': 'Data Action',
  'Data Column Name': 'Data Column Name',
  'Data Column': 'Data Column',
  'Data Metrics': 'Data Metrics',
  'Data added to boundary details': 'Data added to boundary details',
  'Data added to proximity details': 'Data added to proximity details',
  'Data added to territory details': 'Data added to territory details',
  'Data export is disabled': 'Data export is disabled',
  'Data font color / size': 'Data {{what}} font color / size',
  'Data should contain location information such as Address, City, State, Zip/Postal Code.<1></1>You may also check that Step 2, Match Up Your Data, was completed, and/or download our <3>spreadsheet template</3> for help with formatting issues.': 'Data should contain location information such as Address, City, State, Zip/Postal Code.<1></1>You may also check that Step 2, Match Up Your Data, was completed, and/or download our <3>spreadsheet template</3> for help with formatting issues.',
  'Date created': 'Date created',
  'Deactivating 2 Factor Authentication has failed': 'Deactivating 2 Factor Authentication has failed',
  'Decline invitation': 'Decline invitation',
  'Default Fill': 'Default Fill',
  'Default Map': 'Default Map',
  'Default Tool': 'Default Tool',
  'Default Value (Optional)': 'Default Value (Optional)',
  'Delete All Arrows': 'Delete All Arrows',
  'Delete All Callouts': 'Delete All Callouts',
  'Delete All Circles': 'Delete All Circles',
  'Delete All Drawings': 'Delete All Drawings',
  'Delete All Icon Markers': 'Delete All Icon Markers',
  'Delete All Images': 'Delete All Images',
  'Delete All Labels': 'Delete All Labels',
  'Delete All Numbers': 'Delete All Numbers',
  'Delete All Polygons': 'Delete All Polygons',
  'Delete All Polylines': 'Delete All Polylines',
  'Delete All Rectangles': 'Delete All Rectangles',
  'Delete All Texts': 'Delete All Texts',
  'Delete Client Account': 'Delete Client Account',
  'Delete File': 'Delete File',
  'Delete Fill Group': 'Delete Fill Group',
  'Delete Layer': 'Delete Layer',
  'Delete Layered Map': 'Delete Layered Map',
  'Delete Map View': 'Delete Snapshot',
  'Delete Maps': 'Delete Map(s)',
  'Delete Marker': 'Delete Marker',
  'Delete Markers': 'Delete Markers',
  'Delete My Data': 'Delete My Data',
  'Delete Polygon': 'Delete Polygon',
  'Delete Proximity': 'Delete Proximity',
  'Delete Radius': 'Delete Radius',
  'Delete Route': 'Delete Route',
  'Delete Rows': 'Delete Rows',
  'Delete Selected': 'Delete Selected',
  'Delete Territory Group': 'Delete Territory Group',
  'Delete Territory': 'Delete Territory',
  'Delete User': 'Delete User',
  'Delete all locations': 'Delete all locations',
  'Delete all': 'Delete all',
  'Delete element': 'Delete element',
  'Delete territory': 'Delete territory',
  'Deleting a map also deletes all its map views, including map views of other owners': 'Deleting a map also deletes all its map views, including map views of other owners',
  'Demo Videos': 'Demo Videos',
  'Demographic Group': 'Demographic Group',
  'Demote to Member': 'Demote to Member',
  'Direct access works.': 'Direct access works.',
  'Directions Not Found or Over 3 hours': 'Directions Not Found or Over 3 hours',
  'Disable Active Tool Panel': 'Disable Active Tool Panel',
  'Disable User': 'Disable User',
  'Discard Unmapped Rows': 'Discard Unmapped Rows',
  'Disconnect Map': 'Disconnect Map',
  'Display Highways': 'Display Highways',
  'Display Legend Only': 'Display Legend Only',
  'Display Search Button': 'Display Search Button',
  'Display Title & Description': 'Display Title & Description',
  'Display marker number if column is empty': 'Display marker number if column is empty',
  'Distance Calculation Method': 'Distance Calculation Method',
  'Distance Calculator': 'Distance Calculator',
  'Distance Radius Circles': 'Distance Radius Circles',
  'Distance Radius': 'Distance Radius',
  'Distance To Center': 'Distance To Center',
  'Distance to Next Stop': 'Distance to Next Stop',
  'Do not show again for this session': 'Do not show again for this session',
  'Do not show again': 'Do not show again',
  'Does Not Contain': 'Does Not Contain',
  'Does Not End With': 'Does Not End With',
  'Does Not Equal': 'Does Not Equal',
  'Does Not Start With': 'Does Not Start With',
  'Download Started': 'Download Started',
  'Download didn\'t work? Click here to <1>download again</1>': 'Download didn’t work? Click here to <1>download again</1>',
  'Drag and Drop <1>data to hide here.</1>': 'Drag and Drop <1>data to hide here.</1>',
  'Drag and drop to customize ordering': 'Drag and drop to customize ordering',
  'Draw Element': 'Draw Element',
  'Draw Territory': 'Draw Territory',
  'Draw another': 'Draw another',
  'DrawingToolEditableOption.BackgroundColor': 'Background Color',
  'DrawingToolEditableOption.BorderRadius': 'Border Radius',
  'DrawingToolEditableOption.DotColor': 'Dot Color',
  'DrawingToolEditableOption.DotSize': 'Dot Size',
  'DrawingToolEditableOption.DrawingToolNumber': 'Number',
  'DrawingToolEditableOption.DrawingToolSelectedImage': 'Select Image',
  'DrawingToolEditableOption.FillColor': 'Fill Color',
  'DrawingToolEditableOption.FillOpacity': 'Fill Opacity',
  'DrawingToolEditableOption.FontColor': 'Font Color',
  'DrawingToolEditableOption.FontSize': 'Font Size',
  'DrawingToolEditableOption.FontSizeOnCurrentMapZoom': 'Font Size On Current Map Zoom',
  'DrawingToolEditableOption.ImageSize': 'Image Size',
  'DrawingToolEditableOption.ImageSizeOnCurrentMapZoom': 'Image Size On Current Map Zoom',
  'DrawingToolEditableOption.LineStrokeWeight': 'Line Stroke Weight',
  'DrawingToolEditableOption.LineWeight': 'Line Weigth',
  'DrawingToolEditableOption.LineWeightOnCurrentMapZoom': 'Line Weight on Current Map Zoom',
  'DrawingToolEditableOption.Radius': 'Radius',
  'DrawingToolEditableOption.RadiusOnCurrentMapZoom': 'Radius on Current Map Zoom',
  'DrawingToolEditableOption.ScalesWithMapZoom': 'Scales With Map Zoom',
  'DrawingToolEditableOption.ShowImageBorders': 'Show Image Borders',
  'DrawingToolEditableOption.StrokeColor': 'Stroke Color',
  'DrawingToolEditableOption.StrokeOpacity': 'Stroke Opacity',
  'DrawingToolEditableOption.StrokeWeight': 'Stroke Weight',
  'DrawingToolEditableOption.StrokeWeightOnCurrentMapZoom': 'Stroke Weight on Current Map Zoom',
  'DrawingToolEditableOption.TriangleSize': 'Triangle Size',
  'Drawings management is disabled while adding a new element': 'Drawings management is disabled while adding a new element',
  'Drive Time Polygon Name': 'Drive Time Polygon Name',
  'Drive Time Polygon': 'Drive Time Polygon',
  'Drive Time Polygon: {{time}}': 'Drive Time Polygon: {{time}}',
  'Drive Time Polygons': 'Drive Time Polygons',
  'Driving Distance (Driving + Ferry)': 'Driving Distance (Driving + Ferry up to 3 hours)',
  'Duplicate Filter': 'Duplicate Filter',
  'E-mail Address Column': 'E-mail Address Column',
  'ENABLE ROUTE DRAGGING': 'ENABLE ROUTE DRAGGING',
  'EXPORT LOCATIONS TO ROUTING / DIRECTIONS': 'EXPORT LOCATIONS TO ROUTING / DIRECTIONS',
  'Each marker will only be returned for the radius where it is closest to the center.': 'Each marker will only be returned for the radius where it is closest to the center.',
  'Edit Boundaries': 'Edit Boundaries',
  'Edit Data as Group (Batch)': 'Edit Data as Group (Batch)',
  'Edit Data': 'Edit Data',
  'Edit Drive Time Polygon': 'Edit Drive Time Polygon',
  'Edit Existing Territories': 'Edit Existing Territories',
  'Edit Filters': 'Edit Filters',
  'Edit Layered Map': 'Edit Layered Map',
  'Edit Location': 'Edit Location',
  'Edit Marker': 'Edit Marker',
  'Edit Name': 'Edit Name',
  'Edit New Marker Information': 'Edit New Marker Information',
  'Edit Proximity': 'Edit Proximity',
  'Edit Radius': 'Edit Radius',
  'Edit Route': 'Edit Route',
  'Edit Selection': 'Edit Selection',
  'Edit Territory Border': 'Edit Territory Border',
  'Edit Territory Group': 'Edit Territory Group',
  'Edit Territory': 'Edit Territory',
  'Edit your polygon.': 'Edit your polygon.',
  'Edit {{boundaryType}}': 'Edit {{boundaryType}}',
  'Edit {{drawingToolName}}': 'Edit {{drawingToolName}}',
  'Elements cannot be added while editing mode is active': 'Elements cannot be added while editing mode is active',
  'Elements you draw on the map can be viewed and managed here': 'Elements you draw on the map can be viewed and managed here',
  'Email Address': 'Email Address',
  'Email Map Link': 'Email Map Link',
  'Email Navigation Link': 'Email Navigation Link',
  'Email Notification': 'Email Notification',
  'Email Sent': 'Email Sent',
  'Email address change has already been confirmed.': 'Email address change has already been confirmed.',
  'Email address must not be empty': 'Email address must not be empty',
  'Email change has to be confirmed, please check your email.': 'Email change has to be confirmed, please check your email.',
  'Email re-sent': 'Email re-sent',
  'Email was sent!': 'Email was sent!',
  'EmailChangeVerification.ending': 'If you need additional support please contact us at <1>support@maptive.com</1>',
  'EmailChangeVerification.intro': 'You are here because you are logged into a different Maptive account. To resolve this, follow these steps:',
  'EmailChangeVerification.step1': 'Log out of Maptive.',
  'EmailChangeVerification.step2': 'Go back to the verification email and click the link again.',
  'EmailChangeVerification.step3': 'You’ll be directed to log in with your current email address.',
  'Emails do not match': 'Emails do not match',
  'Embed Map Code': 'Embed Map Code',
  'Embed Map on Website, Blog, etc.': 'Embed Map on Website, Blog, etc.',
  'Embed Map': 'Embed Map',
  'EmbedMap.EmbedCodeInstructions': 'Copy and Paste the code below to your website, blog, etc.',
  'Enable POI Click Descriptions': 'Enable POI Click Descriptions',
  'Enable Two-Factor Authentication': 'Enable Two-Factor Authentication',
  'Enable User': 'Enable User',
  'Enable/Disable Column': 'Enable/Disable Column',
  'Enable/Disable Columns on Public Map': 'Enable/Disable Columns on Public Map',
  'Enable/Disable Columns': 'Enable/Disable Columns',
  'Ends With': 'Ends With',
  'Enter Locations': 'Enter Locations',
  'Enter Map Password': 'Enter Map Password',
  'Enter New Password': 'Enter New Password',
  'Enter Search': 'Enter Search',
  'Enter Starting point': 'Enter Starting point',
  'Enter Text': 'Enter Text',
  'Enter code here': 'Enter code here',
  'Enter email address': 'Enter email address',
  'Enter map description (optional)': 'Enter map description (optional)',
  'Enter password': 'Enter password',
  'Enter the one-time code found in your Google Authenticator after scanning the QR code': 'Enter the one-time code found in your Google Authenticator after scanning the QR code',
  'Error Exporting an Image': 'Error Exporting an Image',
  'Error adding area to an existing territory': 'Error adding area to an existing territory',
  'Error creating new territory': 'Error creating new territory',
  'Error exporting data': 'Error exporting data',
  'Error getting group column data': 'Error getting group column data',
  'Error reassigning area': 'Error reassigning area',
  'Error removing area from territory': 'Error removing area from territory',
  'Error removing map': 'Error removing map',
  'Error selecting area using lasso': 'Error selecting area using lasso',
  'Error trying to log in.': 'Error trying to log in.',
  'Exact Word Match': 'Exact Word Match',
  'Example Text': 'Example Text',
  'Exceeded proximity limit of {{limit}} markers': 'Exceeded proximity limit of {{limit}} markers',
  'Existing Groups': 'Existing Groups',
  'Expand All': 'Expand All',
  'Expand Image': 'Expand Image',
  'Expand Tool': 'Expand Tool',
  'Expand snapshots': 'Expand snapshots',
  'Export Boundaries': 'Export Boundaries',
  'Export Contained Boundaries to File': 'Export Contained Boundaries to File',
  'Export Contained Boundaries': 'Export Contained Boundaries',
  'Export Data Visible on Map': 'Export Data Visible on Map',
  'Export Data as': 'Export Data as',
  'Export Data in Group': 'Export Data in Group',
  'Export Data in Territory': 'Export Data in Territory',
  'Export Data with Boundaries': 'Export Data with Boundaries',
  'Export Data with Territories': 'Export Data with Territories',
  'Export Data': 'Export Data',
  'Export Directions Locations': 'Export Directions Locations',
  'Export Entire Dataset': 'Export Entire Dataset',
  'Export Feature': 'Export Feature',
  'Export File Type': 'Export File Type',
  'Export File': 'Export File',
  'Export Filtered Data': 'Export Filtered Data',
  'Export Locations to File': 'Export Locations to File',
  'Export Locations': 'Export Locations',
  'Export Map Image for Printing': 'Export Map Image for Printing',
  'Export Map Image': 'Export Map Image',
  'Export Now': 'Export Now',
  'Export Proximities': 'Export Proximities',
  'Export Sample': 'Export Sample',
  'Export Size': 'Export Size',
  'Export Style': 'Export Style',
  'Export as File': 'Export as File',
  'Export data within Proximity': 'Export data within Proximity',
  'ExportImageMode.DisabledOption.LegendNotAvailable': 'To be able to select this Export Style a column needs to be active in the Grouping Tool or you need to have a Boundary or a Territory added to the map.',
  'ExportImageMode.DisabledOption.NoVisibleMarkersOnMap': 'There are no visible markers on the map so there is nothing to print.',
  'ExportImageMode.DisabledOption.TooManyMarkers': 'Your map currently represents too many markers, please zoom in or filter out your data to print the locations list.',
  'ExportImageMode.Legend__description': '(download an image of the complete legend)',
  'ExportImageMode.Legend__label': 'Legend',
  'ExportImageMode.LocationList__description': '(download an image of the location list)',
  'ExportImageMode.LocationList__label': 'Location List',
  'ExportImageMode.MapAndLegend__description': '(download an image of the map with simplified legend)',
  'ExportImageMode.MapAndLegend__label': 'Map + Legend',
  'ExportImageMode.Map__description': '(download an image of the map without any menus)',
  'ExportImageMode.Map__label': 'Map',
  'ExportImageMode.Screenshot__description': '(download an image of what you have on screen)',
  'ExportImageMode.Screenshot__label': 'Screenshot',
  'Fade When Inactive': 'Fade When Inactive',
  'Failed to Create a Layered Map': 'Failed to Create a Layered Map',
  'Failed to Edit a Layered Map': 'Failed to Edit a Layered Map',
  'Failed to Update Subscription.': 'Failed to Update Subscription.',
  'Failed to create optimised territories': 'Failed to create optimised territories',
  'Failed to fetch data': 'Failed to fetch data',
  'Failed to find route for unknown reason.': 'Failed to find route for unknown reason.',
  'Failed to move markers': 'Failed to move markers',
  'Fill Based on Data': 'Fill Based on Data',
  'Fill Color': 'Fill Color',
  'Fill Opacity': 'Fill Opacity',
  'Filter By': 'Filter By',
  'Filter Data / Locations': 'Filter Data / Locations',
  'Filter Data': 'Filter Data',
  'Filter Maps': 'Filter Maps',
  'Filter Menus Open': 'Filter Menus Open',
  'Filter Type': 'Filter Type',
  'Filter clause with the same value already exists': 'Filter clause with the same value already exists',
  'Finalizing the Balancing of Territories': 'Finalizing the Balancing of Territories',
  'Find Map View': 'Find Map View',
  'Find Now': 'Find Now',
  'Find Proximity': 'Find Proximity',
  'Find The Closest Locations': 'Find The Closest Locations',
  'Finish Geocoding': 'Finish Geocoding',
  'First Name': 'First Name',
  'First name cannot be empty!': 'First name cannot be empty!',
  'First name must not be empty': 'First name must not be empty',
  'For subscription to Auto Territory Builder please contact <1>support</1>': 'For subscription to Auto Territory Builder please contact <1>Maptive Support</1>',
  'Forgot Password?': 'Forgot Password?',
  'From Boundaries': 'From Boundaries',
  'From Here': 'From Here',
  'From Territories': 'From Territories',
  'From list': 'From list',
  'From map': 'From map',
  'From {{from}} to {{to}}': 'From {{from}} to {{to}}',
  'Full & partial boundaries': 'Full & partial boundaries',
  'Full & partial territories': 'Full & partial territories',
  'Full boundaries only': 'Full boundaries only',
  'Full territories only': 'Full territories only',
  'Functionality under control by Grouping Tool': 'Row is used for grouping',
  'General Label Above Markers Settings': 'General Label Above Markers Settings',
  'General Label Marker Settings': 'General Label Marker Settings',
  'General Marker Settings': 'General Marker Settings',
  'Generated territories are not permanent': 'Generated territories are not permanent',
  'Generating New Territories': 'Generating New Territories',
  'Geocoding in progress': 'Geocoding in progress',
  'Geographic Boundary': 'Geographic Boundary',
  'Geographic Location': 'Geographic Location',
  'Get Directions': 'Get Directions',
  'Go To Map': 'Go To Map',
  'Go To Page': 'Go To Page',
  'Go to Login Page': 'Go to Login Page',
  'Google Authenticator': 'Google Authenticator',
  'Google Sheet Columns': 'Google Sheet Columns',
  'Google Sheets': 'Google Sheets',
  'Google reCaptcha has detected potential suspicious behaviour from your device or network. No email was sent.': 'Google reCaptcha has detected potential suspicious behaviour from your device or network. No email was sent.',
  'Google\'s Predicted Location': 'Google’s Predicted Location',
  'Greetings from home. <1>Log in.</1>': 'Greetings from home. <1>Log in.</1>',
  'Group Areas Using Data': 'Group Areas Using Data',
  'Group By Numerical or % Value Ranges': 'Group By Numerical or % Value Ranges',
  'Group Count': 'Group Count',
  'Group Markers Settings': 'Group Markers Settings',
  'Group Name': 'Group Name',
  'Group Names': 'Group Names',
  'Group Now': 'Group Now',
  'Group Tie': 'Group Tie',
  'Grouping / Colored Pins': 'Grouping / Colored Pins',
  'Grouping Column: {{columnName}}': 'Grouping Column: {{columnName}}',
  'Grouping Method': 'Grouping Method',
  'GroupingToolHint.BulletPoint1': '<b>Efficient bulk customization</b> - Modify / color numerous markers simultaneously based on a column / variable in your data set. This method will <b>automatically and in real time</b> change the color or styles of markers when they fall into a new group or category in your data when it’s updated. This is a much better option than manually changing marker color / styles. For example, say you have <GreenEmphasis>sales leads</GreenEmphasis> as green markers and <RedEmphasis>active sales</RedEmphasis> as red markers. When a “sales lead” changes to an “active sale” in your data the marker will automatically change colors for you if you use the grouping tool for marker customization.',
  'GroupingToolHint.BulletPoint2': '<b>Multi-column color groupings</b> - Gain nuanced control by grouping / multi-coloring markers using up to two data columns / metrics. The color, shape, size of your markers will visually express multiple information metrics of each location in your data as opposed to just one.',
  'GroupingToolHint.Caption': 'Streamline Your Work With the Grouping Tool',
  'GroupingToolHint.DemoParagraph': 'Before you continue with each marker individually, why not give the <GroupingToolLink>Grouping Tool</GroupingToolLink> a try? Or <ContactUsLink>contact us</ContactUsLink> for a demo of how it works.',
  'GroupingToolHint.Intro': 'It seems you’ve been doing a lot of individual marker customizations. Most likely, you’re trying to custom color your markers or change marker styles. Individual map marker customizations really only make sense to use when you’re working with a very small set of locations (10 or less) OR wanting to upload an image to use as a marker to represent a specific location. Did you know you can save time and better customize map markers with our <GroupingToolLink>Grouping Tool</GroupingToolLink>?',
  'GroupingToolHint.Outro': 'Check out this handy tutorial video for a visual explanation.',
  'Growing Pin Markers': 'Growing Pin Markers',
  'Heat Map: {{heatMapName}}': 'Heat Map: {{heatMapName}}',
  'Heat Mapping': 'Heat Mapping',
  'Hide Areas w/o Data': 'Hide Areas w/o Data',
  'Hide Boundary Labels': 'Hide Boundary Labels',
  'Hide Items': 'Hide Items',
  'Hide Labels': 'Hide Labels',
  'Hide Proximity': 'Hide Proximity',
  'Hide Radius': 'Hide Radius',
  'Hide Small Radii': 'Hide Small Radii',
  'Hide Territory Labels': 'Hide Territory Labels',
  'Hide street view image': 'Hide street view image',
  'High Value': 'High Value',
  'High Values': 'High Values',
  'High Volume Marker Display': 'High Volume Marker Display',
  'Hint: You may enter multiple values separated with commas.': 'Hint: You may enter multiple values separated with commas.',
  'Hover the outline and click the remove button to delete the vertex.': 'Hover the outline and click the remove button to delete the vertex.',
  'Hover your mouse over areas which you want to add to your selection.': 'Hover your mouse over areas which you want to add to your selection.',
  'How do you want to display your directions?': 'How do you want to display your directions?',
  'How do you want to set your initial map position?': 'How do you want to set your initial map position?',
  'I will set the latitude, longitude and map zoom myself': 'I will set the latitude, longitude and map zoom myself',
  'Icon Marker Element': 'Icon Marker Element',
  'Icon Markers': 'Icon Markers',
  'Id Column': 'Id Column',
  'If left blank, all the new column cells will be empty by default.': 'If left blank, all the new column cells will be empty by default.',
  'Ignore Filters is activated.': 'Ignore Filters is activated.',
  'Ignore Filters': 'Ignore Filters',
  'Image Element': 'Image Element',
  'Image URL Column': 'Image URL Column',
  'Image library': 'Image library',
  'ImageSize.Large__description': '(Poster size, Good for display)',
  'ImageSize.Large__label': 'Large',
  'ImageSize.Normal__description': '(The same size as your browser window. Image size)',
  'ImageSize.Normal__label': 'Normal',
  'Images selected for this location': 'Images selected for this location',
  'Import Data': 'Import Data',
  'Import New Data': 'Import New Data',
  'Import and add to existing data set.': 'Import and add to existing data set.',
  'Import and replace existing data set.': 'Import and replace existing data set.',
  'In order to accept {{client}}&apos;s invitation, please contact Maptive&apos;s customer support so you can instruct us as to how you want to treat your existing Maptive account.': 'In order to accept {{client}}’s invitation, please contact Maptive’s customer support so you can instruct us as to how you want to treat your existing Maptive account.',
  'In this step you may add OR...': 'In this step you may add OR remove base maps to your layered map. If you wish to do both, do one action first and then launch this modal again. If you with to do neither, just omit this step.',
  'Include only': 'Include only',
  'Include only:': 'Include only:',
  'Independent Member': 'Independent Member',
  'Individual Location': 'Individual Location',
  'Individual Markers Settings': 'Individual Markers Settings',
  'Initial map position': 'Initial map position',
  'Initializing Territories': 'Initializing Territories',
  'Input Address Information (Address, City, Latitude, etc)': 'Input Address Information (Address, City, Latitude, etc)',
  'Integrated Columns': 'Integrated Columns',
  'Invalid 2FA code. Please check and try again.': 'Invalid 2FA code. Please check and try again.',
  'Invalid Column for Grouping': 'Invalid Column for Grouping',
  'Invalid Data Column': 'Invalid Data Column',
  'Invalid Filter Type': 'Invalid Filter Type',
  'Invalid email address': 'Invalid email address',
  'Invalid email!': 'Invalid email!',
  'Invalid file type.': 'Invalid file type.',
  'Invitation accepted!': 'Invitation accepted!',
  'Invitation successfully declined!': 'Invitation successfully declined!',
  'Invite Users - separate emails with commas': 'Invite Users - separate emails with commas',
  'Is Empty': 'Is Empty',
  'Is In': 'Is In',
  'Is Not Empty': 'Is Not Empty',
  'Is Not In': 'Is Not In',
  'Isolate {{boundaryType}} tooManyPolygons': 'The {{boundaryType}} contains too many polygons for the Isolate functionality.',
  'Isolate {{boundaryType}}': 'Isolate {{boundaryType}}',
  'Isolated: {{name}}': 'Isolated: {{name}}',
  'Issue finding locations': 'Issue finding locations',
  'It may take 1-2 minutes before you start to see your data on the map.': 'It may take 1-2 minutes before you start to see your data on the map.',
  'It may take a while before you start to see your maps.': 'It may take a while before you start to see your maps.',
  'It\'s possible the map creator deleted the map or the data no longer exists.': 'It’s possible the map creator deleted the map or the data no longer exists.',
  'Just click the "FIND NOW" button to find the location that\'s closest to your current location ... OR ... Enter an address, zip code, city, etc, etc..': 'Just click the "FIND NOW" button to find the location that’s closest to your current location ... OR ... Enter an address, zip code, city, etc, etc..',
  'Just this Radius': 'Just this Radius',
  'Label Element': 'Label Element',
  'Labels Above Markers (from data)': 'Labels Above Markers (from data)',
  'Labels Above Markers Settings': 'Labels Above Markers Settings',
  'Labels Color': 'Labels Color',
  'Labels Inside Markers (from data)': 'Labels Inside Markers (from data)',
  'Labels automatically hidden to improve performance. Zoom in to unhide.': 'Labels automatically hidden to improve performance. Zoom in to unhide.',
  'Landscape Color': 'Landscape Color',
  'Lasso & Fill Locations': 'Lasso & Fill Locations',
  'Lasso Tool Options': 'Lasso Tool Options',
  'Lasso Tool': 'Lasso Tool',
  'Lasso tool': 'Lasso tool',
  'Last Name': 'Last Name',
  'Last Updated': 'Last Updated',
  'Last name cannot be empty!': 'Last name cannot be empty!',
  'Last name must not be empty': 'Last name must not be empty',
  'Last updated': 'Last updated',
  'Launch Payment Portal': 'Launch Payment Portal',
  'Launch Street View': 'Launch Street View',
  'Layer Map Now': 'Layer Map Now',
  'Layer Multiple Maps': 'Layer Multiple Maps',
  'Layered Map Dependencies - Confirm Actions': 'Layered Map Dependencies - Confirm Actions',
  'Layered Map Dependencies {{mapNumber}} of {{mapsTotal}}': 'Layered Map Dependencies {{mapNumber}} of {{mapsTotal}}',
  'Layering Maps': 'Layering Maps',
  'Least Recently Created Maps': 'Least Recently Created Maps',
  'Least Recently Migrated Maps': 'Least Recently Migrated Maps',
  'Least Recently Updated Maps': 'Least Recently Updated Maps',
  'Legend Replaces Grouping': 'Legend Replaces Grouping',
  'Less important': 'Less important',
  'License Expired': 'License Expired',
  'License Status': 'License Status',
  'License Type': 'License Type',
  'Light Blue': 'Light Blue',
  'Light Gray': 'Light Gray',
  'Limit Markers per Radius': 'Limit Markers per Radius',
  'Limit Results': 'Limit Results',
  'Line Color': 'Line Color',
  'Line Width': 'Line Width',
  'Link copied': 'Link copied',
  'Live Chat': 'Live Chat',
  'Load Boundaries': 'Load Boundaries',
  'Load Directions': 'Load Directions',
  'Load Existing Territory Group': 'Load Existing Territory Group',
  'Load Geographic Boundary': 'Load Geographic Boundary',
  'Load More': 'Load More',
  'Load More...': 'Load More...',
  'Load Saved Territory Group': 'Load Saved Territory Group',
  'Load Territories': 'Load Territories',
  'Load Territory Groups': 'Load Territory Groups',
  'Loading Your Boundaries': 'Loading Your Boundaries',
  'Loading Your Selection': 'Loading Your Selection',
  'Loading...': 'Loading...',
  'Location Density': 'Location Density',
  'Location Finder Starts Open': 'Location Finder Starts Open',
  'Location Finder': 'Location Finder',
  'Location Images': 'Location Images',
  'Location List Open': 'Location List Open',
  'Location List': 'Location List',
  'Location Name / Title': 'Location Name / Title',
  'Location current of total': 'Location {{current}} of {{total}}',
  'Locations Currently Visible On The Map': 'Locations Currently Visible On The Map',
  'Locations List': 'Locations List',
  'Log Out': 'Log Out',
  'Log in here': 'Log in here',
  'Login as User': 'Login as User',
  'Low Value': 'Low Value',
  'Low Values': 'Low Values',
  'MATCHES TO': 'MATCHES TO',
  'MI/KM Measurement': 'MI/KM Measurement',
  'Make Map Disconnected': 'Make Map Disconnected',
  'Make New Account Owner': 'Make New Account Owner',
  'Make your map publicly available for view. You will be able to add password protection.': 'Make your map publicly available for view. You will be able to add password protection.',
  'Manage Drawing Elements': 'Manage Drawing Elements',
  'Manage Subscription': 'Manage Subscription',
  'Manage Team Users': 'Manage Team Users',
  'Manage Territory Groups': 'Manage Territory Groups',
  'Manage Users': 'Manage Users',
  'Manual Method': 'Manual Method',
  'Manually create a New Map from scratch with no data set.': 'Manually create a New Map from scratch with no data set.',
  'Map / saved map view name': 'Map / saved map view name',
  'Map Control': 'Map Control',
  'Map Description (optional)': 'Map Description (optional)',
  'Map Description': 'Map Description',
  'Map Directions / Routing Menu Default Settings': 'Map Directions / Routing Menu Default Settings',
  'Map Image Export': 'Map Image Export',
  'Map Legend': 'Map Legend',
  'Map Marker Style': 'Map Marker Style',
  'Map Markers / Graphics': 'Map Markers / Graphics',
  'Map Markers': 'Map Markers',
  'Map Migration Finished': 'Map Migration Finished',
  'Map Migration in Progress': 'Map Migration in Progress',
  'Map Name': 'Map Name',
  'Map Name: ': 'Map Name: ',
  'Map Now': 'Map Now',
  'Map Placeholder': 'Map Placeholder',
  'Map Protection Status': 'Map Protection Status',
  'Map Security Status': 'Map Security Status',
  'Map Security': 'Map Security',
  'Map Settings Out of Sync': 'Map Settings Out of Sync',
  'Map Settings': 'Map Settings',
  'Map Styles': 'Map Styles',
  'Map Tools Menu Open': 'Map Tools Menu Open',
  'Map Tools': 'Map Tools',
  'Map View Name Is Empty': 'Map View Name Is Empty',
  'Map View Name': 'Map View Name',
  'Map View': 'Map View',
  'Map With Sales People Creation Success': 'Map With Sales People Creation Success',
  'Map With Sales People Name': 'Map With Sales People Name',
  'Map Zoom': 'Map Zoom',
  'Map creation failed': 'Map creation failed',
  'Map old spreadsheet columns to new': 'Map old spreadsheet columns to new',
  'Map our sample data set by clicking Map Now.': 'Map our sample data set by clicking Map Now.',
  'Map restore failed': 'Map restore failed',
  'Map styles': 'Map styles',
  'Maps based on <1>{{mapName}}</1> - owned by others': 'Maps based on <1>{{mapName}}</1> - owned by others',
  'Maps based on <1>{{mapName}}</1> - you own': 'Maps based on <1>{{mapName}}</1> - you own',
  'Maptive Columns': 'Maptive Columns',
  'Maptive Support': 'Maptive Support',
  'Marker Label Default Settings': 'Marker Label Default Settings',
  'Marker Preview': 'Marker Preview',
  'Marker Settings': 'Marker Settings',
  'Marker {{name}}': 'Marker: {{name}}',
  'Marker {{rowId}}': 'Marker {{rowId}}',
  'Markers Closest to the Center': 'Markers Closest to the Center',
  'Markers Density': 'Markers Density',
  'Markers Visibility Button': 'Markers Visibility Button',
  'Markers have been recently added or changed by another user. We recommend you to refresh the map page to see the latest data before making any changes.': 'Markers have been recently added or changed by another user. We recommend you to refresh the map page to see the latest data before making any changes.',
  'Markers have been recently changed by another user. Please refresh your page in order to be able to make spreadsheet data changes.': 'Markers have been recently changed by another user. Please refresh your page in order to be able to make spreadsheet data changes.',
  'Markers inside Radii and Polygons': 'Markers inside Radii and Polygons',
  'Match location with boundaries based on': 'Match location with boundaries based on',
  'Match up Location Related Data': 'Match up Location Related Data',
  'Match-up Map Columns': 'Match-up Map Columns',
  'Measure Distance': 'Measure Distance',
  'Measure Straight-line Distance on Map': 'Measure Straight-line Distance on Map',
  'Medium Values': 'Medium Values',
  'More about location': 'More about location',
  'More important': 'More important',
  'More than {{percentage}}% of values in this column are considered non numerical.': 'More than {{percentage}}% of values in this column are considered non numerical.',
  'Most Recently Created Maps': 'Most Recently Created Maps',
  'Most Recently Migrated Maps': 'Most Recently Migrated Maps',
  'Most Recently Updated Maps': 'Most Recently Updated Maps',
  'Move Labels': 'Move Labels',
  'Move Map Marker Now Active': 'Move Map Marker Now Active',
  'Move Markers': 'Move Markers',
  'Multi Color Bubble Markers': 'Multi Color Bubble Markers',
  'Multiple Point Distance Measurement (A to B to C to D)': 'Multiple Point Distance Measurement (A to B to C to D)',
  'Multiple Point': 'Multiple Point',
  'My Current Location': 'My Current Location',
  'My Google SpreadSheets': 'My Google SpreadSheets',
  'My Location': 'My Location',
  'My Maps': 'My Maps',
  'N/A': 'N/A',
  'NOTE: <1>Applying changes</1> will permanently replace the data on this existing layered map and refresh the page.': 'NOTE: <1>Applying changes</1> will permanently replace the data on this existing layered map and refresh the page.',
  'Name Territory Group': 'Name Territory Group',
  'Name Your Boundary Group': 'Name Your Boundary Group',
  'Name Your Map': 'Name Your Map',
  'Name Your Sales Rep Location Map': 'Name Your Sales Rep Location Map',
  'Name or Email:': 'Name or Email:',
  'Name your territory': 'Name your territory',
  'Near me': 'Near me',
  'Need Help?': 'Need Help?',
  'Never for this base map': 'Never for this base map',
  'New Columns': 'New Columns',
  'New Email Address': 'New Email Address',
  'New Map Name': 'New Map Name',
  'New Territory Group Name': 'New Territory Group Name',
  'New Updates': 'New Updates',
  'New Value / Text': 'New Value / Text',
  'Newest to Oldest Maps': 'Newest to Oldest Maps',
  'Next Billing Date': 'Next Billing Date',
  'No Data Columns Available': 'No Data Columns Available',
  'No Drawn Elements': 'No Drawn Elements',
  'No Group Selected': 'No Group Selected',
  'No Limit': 'No Limit',
  'No Locations': 'No Locations',
  'No Markers Selected': 'No Markers Selected',
  'No Match': 'No Match',
  'No Metrics Selected': 'No Metrics Selected',
  'No Saved Map Views Match Your Search': 'No Saved Map Views Match Your Search',
  'No Saved Map Views': 'No Saved Map Views',
  'No Saved Proximities Match Your Search': 'No Saved Proximities Match Your Search',
  'No Saved Proximities': 'No Saved Proximities',
  'No Territory Fill': 'No Territory Fill',
  'No Tools': 'No Tools',
  'No Visible Locations': 'No Visible Locations',
  'No boundaries to export': 'No boundaries to export',
  'No credit card required. No surprises. Just results.': 'No credit card required. No surprises. Just results.',
  'No data sources present.': 'No data sources present.',
  'No demographics data available for selected building block type': 'No demographics data available for selected building block type',
  'No locations to export': 'No locations to export',
  'No results are matching your query. Update your filters to see the data.': 'No results are matching your query. Update your filters to see the data.',
  'No results': 'No results',
  'No route found.': 'No route found.',
  'No rows found in the selected Google Sheet': 'No rows found in the selected Google Sheet',
  'No territories': 'No territories',
  'No territory groups found': 'No territory groups found',
  'No territory groups': 'No territory groups',
  'No {{columnName}}': 'No {{columnName}}',
  'Non-numerical Values': 'Non-numerical Values',
  'Not Now': 'Not Now',
  'Not a numerical column': 'Not a numerical column',
  'Not logged into Google account': 'Not logged into Google account',
  'Note: Once disconnected, data cannot be connected again.': 'Note: Once disconnected, data cannot be connected again.',
  'Note: You are about to convert your fill groups to a territory group. The original boundaries / territories will get removed from your map by doing so. You can re-add them any time.': 'Note: You are about to convert your fill groups to a territory group. The original boundaries / territories will get removed from your map by doing so. You can re-add them any time.',
  'Note: You can continue working on your work offline. You won\'t be able to save you progress in offline mode.': 'Note: You can continue working on your work offline. You won’t be able to save you progress in offline mode.',
  'Note:': 'Note:',
  'Notice Group Limit Reached': 'Notice: Group Limit Reached',
  'Number Element': 'Number Element',
  'Number Of Groups': 'Number Of Groups',
  'Number Of Ranges': 'Number Of Ranges',
  'Number Ranges': 'Number Ranges',
  'Numeric Label Markers (1,2,3...)': 'Numeric Label Markers (1,2,3...)',
  'Numerical {{name}}': 'Numerical: {{name}}',
  'Oh no, something went wrong': 'Oh no, something went wrong',
  'Old Columns': 'Old Columns',
  'Oldest to Newest Maps': 'Oldest to Newest Maps',
  'On Click': 'On Click',
  'On Hover': 'On Hover',
  'Only people with the login to your Maptive account can view the map.': 'Only people with the login to your Maptive account can view the map.',
  'Only people with the password can view.': 'Only people with the password can view.',
  'Only the owner ({{ownerName}}) is allowed to delete this territory group': 'Only the owner ({{ownerName}}) is allowed to delete this territory group',
  'Only the owner ({{ownerName}}) is allowed to edit this territory group': 'Only the owner ({{ownerName}}) is allowed to edit this territory group',
  'Only the owner is allowed to delete their map views.': 'Only the owner is allowed to delete their map views.',
  'Only the owner is allowed to delete their maps.': 'Only the owner is allowed to delete their maps.',
  'Only the owner is allowed to modify their map views.': 'Only the owner is allowed to modify their map views.',
  'Open Group Marker Settings': 'Open Group Marker Settings',
  'Open Links in Same Window': 'Open Links in Same Window',
  'Open Territory Group': 'Open Territory Group',
  'Open in Presentation/Share Mode': 'Open in Presentation/Share Mode',
  'Optimization with constraints': 'Optimization with constraints',
  'Optimize Driving Route': 'Optimize Driving Route',
  'Optimize Route To All Locations': 'Optimize Route To All Locations',
  'Optimize Route': 'Optimize Route',
  'Other Settings': 'Other Settings',
  'Override your Spreadsheet Data': 'Override your Spreadsheet Data',
  'Overwrite settings of marker groups': 'Overwrite settings of marker groups',
  'PageError.QueryStringParamMissing': 'Oops! It looks like you landed on a page that needs some additional information to work properly. This extra information is usually added to the web address (URL) as \'query strings.\' If you’re unsure how to proceed, please go back to the previous page and try navigating from there, or feel free to reach out for help!',
  'Partial Word Match': 'Partial Word Match',
  'Password Protected': 'Password Protected',
  'Password Reset Sent!': 'Password Reset Sent!',
  'Password minimum 1 capital and 1 lowercase letter.': 'At least 1 capital and 1 lowercase',
  'Password minimum 1 number and 1 special symbol.': 'At least 1 number and 1 symbol',
  'Password minimum 8 characters': 'Minimum 8 characters',
  'Password must not be empty': 'Password must not be empty',
  'Passwords do not match': 'Passwords do not match',
  'Paste Data': 'Paste Data',
  'Pause Drawing': 'Pause Drawing',
  'Pay Now': 'Pay Now',
  'Payment issue': 'Payment issue',
  'Percentage Ranges': 'Percentage Ranges',
  'Performance Notice': 'Performance Notice',
  'Permissions Level': 'Permissions Level',
  'Phone Number': 'Phone Number',
  'Pie chart metrics': 'Pie Chart Metrics',
  'Place a Marker on the Map': 'Place a Marker on the Map',
  'Please add at least 2 waypoints to continue.': 'Please add at least 2 waypoints to continue.',
  'Please add password to the main map to be able to password protect your Map View.': 'Please add password to the main map to be able to password protect your Map View.',
  'Please check your payment details and try again, or contact our <1>customer support</1> for assistance if the problem persists.': 'Please check your payment details and try again, or contact our <1>customer support</1> for assistance if the problem persists.',
  'Please enter the code we have just sent to your E-mail address': 'Please enter the code we have just sent to your E-mail address',
  'Please enter the one-time code found in your Google Authenticator': 'Please enter the one-time code found in your Google Authenticator',
  'Please fix the waypoints addresses and try again.': 'Please fix the waypoints addresses and try again.',
  'Please note that after changing the URL address, the previous link will not work.': 'Please note that after changing the URL address, the previous link will not work.',
  'Please refresh your page in order to be able to save your progress. Your changes can not be saved{{endingChar}}': 'Please refresh your page in order to be able to save your progress. Your changes can not be saved{{endingChar}}',
  'Please remove password from the main map to be able to make your Map View public.': 'Please remove password from the main map to be able to make your Map View public.',
  'Please select how you want to remove the <1>{{mapName}}</1> from the following layered maps.': 'Please select how you want to remove the <1>{{mapName}}</1> from the following layered maps.',
  'Please select how you wish this irregularity to be resolved.': 'Please select how you wish this irregularity to be resolved.',
  'Please try again. If this problem persists, please contact the customer support.': 'Please try again. If this problem persists, please contact the customer support.',
  'Please verify or select the correct column in your data that matches "{{columnName}}"': 'Please verify or select the correct column in your data that matches "{{columnName}}"',
  'Point of Interest Color': 'Point of Interest Color',
  'Polygon Element': 'Polygon Element',
  'Polyline Element': 'Polyline Element',
  'Potentially out of date': 'Potentially out of date',
  'Primary Group': 'Primary Group',
  'Proceed anyway': 'Proceed anyway',
  'Processing Finished': 'Processing Finished',
  'Processing Started': 'Processing Started',
  'Processing...': 'Processing...',
  'Promote to Admin': 'Promote to Admin',
  'Provide a supported file (xls, xlsx, csv, tsv) no larger than {{fileSize}}MB': 'Provide a supported file (xls, xlsx, csv, tsv) no bigger than {{fileSize}}MB',
  'Provide valid email address': 'Provide valid email address',
  'Proximity (Travel Time)': 'Proximity (Travel Time)',
  'Proximity Color': 'Proximity Color',
  'Proximity Fill Color': 'Proximity Fill Color',
  'Proximity Fill Opacity': 'Proximity Fill Opacity',
  'Proximity Name': 'Proximity Name',
  'Proximity Radius{{subtype}}: {{distance}}{{unit}}': 'Proximity Radius{{subtype}}: {{distance}}{{unit}}',
  'Proximity Within': 'Proximity Within',
  'Proximity: {{index}}. {{name}}, {{distance}}{{unit}}': 'Radius: {{index}}. {{name}}, {{distance}}{{unit}}',
  'Proximity: {{index}}. {{name}}, {{time}}': 'Drive Time: {{index}}. {{name}}, {{time}}',
  'Public Map Settings': 'Public Map Settings',
  'Public Map Unavailable': 'Public Map Unavailable',
  'Public Map Views': 'Public Map Views',
  'Purchase Maptive': 'Purchase Maptive',
  'Purchase Now': 'Purchase Now',
  'Radius / Drive Time': 'Radius / Drive Time',
  'Radius Name': 'Radius Name',
  'Randomized Fill': 'Randomized Fill',
  'Range Colors': 'Range Colors',
  'Re-enter new password': 'Re-enter new password',
  'Re-send email verification link': 'Re-send email verification link',
  'Rebalancing Territories': 'Rebalancing Territories',
  'Recalculate Equal Ranges': 'Recalculate Equal Ranges',
  'Receiving finalized Territories': 'Receiving finalized Territories',
  'Reconnect Google Spreadsheet': 'Reconnect Google Spreadsheet',
  'Rectangle Element': 'Rectangle Element',
  'Refine your search and try again': 'Refine your search and try again',
  'Refresh Boundaries': 'Refresh Boundaries',
  'Refresh Page': 'Refresh Page',
  'Refresh Territories': 'Refresh Territories',
  'Remap Data Rows': 'Remap Data Rows',
  'Remember For This Map': 'Remember For This Map',
  'Remember me': 'Remember me for {{numberOfDays}} days',
  'Remove All Drawings': 'Remove All Drawings',
  'Remove Boundary from Map': 'Remove Boundary from Map',
  'Remove Element': 'Remove Element',
  'Remove Elements': 'Remove Elements',
  'Remove Member From Map': 'Remove Member From Map',
  'Remove Territory Group from Map': 'Remove Territory Group from Map',
  'Remove Territory': 'Remove Territory',
  'Remove base maps': 'Remove base maps',
  'Remove boundaries from fill group': 'Remove boundaries from fill group',
  'Remove {{entity}} from map': 'Remove {{entity}} from map',
  'Replace Spreadsheet': 'Replace Spreadsheet',
  'Replace existing directions': 'Replace existing directions',
  'Reposition Labels': 'Reposition Labels',
  'Represents Marker Density': 'Represents Marker Density',
  'Represents Numerical Data': 'Represents Numerical Data',
  'Resend Email': 'Resend Email',
  'Reset Changes': 'Reset Changes',
  'Reset Filters': 'Reset Filters',
  'Reset Password': 'Reset Password',
  'Resolve Now': 'Resolve Now',
  'Restore Default Colors': 'Restore Default Colors',
  'Restore Default For': 'Restore Default For',
  'Restore Default': 'Restore Default',
  'Restore Defaults': 'Restore Defaults',
  'Restrict Map Panning': 'Restrict Map Panning',
  'Restrict Zoom Levels': 'Restrict Zoom Levels',
  'Resume Drawing': 'Resume Drawing',
  'Return All': 'Return All',
  'Return marker only once': 'Return marker only once',
  'Revoke Invite': 'Revoke Invite',
  'Roads Color': 'Roads Color',
  'Route #{{number}}': 'Route #{{number}}',
  'Route Color': 'Route Color',
  'Route Directions Color': 'Route Directions Color',
  'Route Name': 'Route Name',
  'Route waypoints cannot be customized.': 'Route waypoints cannot be customized.',
  'RouteCollapsingLevel.Routes__description': 'Stops in each route and turn by turn directions in between each stop will be collapsed by default.',
  'RouteCollapsingLevel.Routes__label': 'SHOW ROUTE LEVEL',
  'RouteCollapsingLevel.Stops__description': 'Turn by turn directions will be collapsed by default.',
  'RouteCollapsingLevel.Stops__label': 'SHOW ROUTES & STOPS',
  'RouteCollapsingLevel.TurnByTurn__description': 'All directions information will be expanded and visible by default.',
  'RouteCollapsingLevel.TurnByTurn__label': 'SHOW ROUTES, STOPS, AND TURN BY TURN',
  'Routing / Directions': 'Routing / Directions',
  'SELECT DEFAULT VIEW': 'SELECT DEFAULT VIEW',
  'SELECT SEARCH MATCHING BEHAVIOR': 'SELECT SEARCH MATCHING BEHAVIOR',
  'SELECT THE ACTIONS YOU WISH TO UNDO': 'SELECT THE ACTIONS YOU WISH TO UNDO',
  'Salesforce maps cannot have their security status changed.': 'Salesforce maps cannot have their security status changed.',
  'Same heatmap already exists': 'Same heatmap already exists',
  'Satellite View': 'Satellite View',
  'Save & Share': 'Save & Share',
  'Save Changes': 'Save Changes',
  'Save Custom Label': 'Save Custom Label',
  'Save Map View': 'Save Map View',
  'Save and Create Another': 'Save and Create Another',
  'Save and Keep Editing': 'Save and Keep Editing',
  'Save to this Map View': 'Save to this Map View',
  'Saved Groups': 'Saved Groups',
  'Saved Map Views': 'Saved Map Views',
  'Scales With Map Zoom': 'Scales With Map Zoom',
  'Search Bar Always Visible': 'Search Bar Always Visible',
  'Search Criteria': 'Search Criteria',
  'Search Data Set': 'Search Data Set',
  'Search For The Nearest Location': 'Search For The Nearest Location',
  'Search Settings': 'Search Settings',
  'Search Your Maps': 'Search Your Maps',
  'Search...': 'Search...',
  'SearchMatchingBehaviour.ShowAllMarkers__description': 'Highlight matching markers but continue to show all markers on map.',
  'SearchMatchingBehaviour.ShowOnlyMatches__description': 'Only show search matches on my map (hide non-matching markers)',
  'Secondary Group': 'Secondary Group',
  'Select & Save': 'Select & Save',
  'Select A Column...': 'Select A Column...',
  'Select A Group...': 'Select A Group...',
  'Select Action': 'Select Action',
  'Select Another User...': 'Select Another User...',
  'Select Boundaries to Export': 'Select Boundaries to Export',
  'Select Boundary Type': 'Select Boundary Type',
  'Select Boundary': 'Select Boundary',
  'Select Category Name': 'Select Category Name',
  'Select Column Name': 'Select Column Name',
  'Select Data Column': 'Select Data Column',
  'Select Data Source': 'Select Data Source',
  'Select Data to Export': 'Select Data to Export',
  'Select Demographic Group': 'Select Demographic Group',
  'Select Demographic': 'Select Demographic',
  'Select Desired Action': 'Select Desired Action',
  'Select Desired Option': 'Select Desired Option',
  'Select Draw action in the menu on the left.': 'Select Draw action in the menu on the left.',
  'Select Export Method': 'Select Export Method',
  'Select Fill Data': 'Select Fill Data',
  'Select Fill Group': 'Select Fill Group',
  'Select Fill Option': 'Select Fill Option',
  'Select Fill': 'Select Fill',
  'Select From': 'Select From',
  'Select Function': 'Select Function',
  'Select Group Column': 'Select Group Column',
  'Select Group': 'Select Group',
  'Select Heat Map Style': 'Select Heat Map Style',
  'Select Import Method': 'Import Method',
  'Select Indicator Color': 'Select Indicator Color',
  'Select Indicator Size': 'Select Indicator Size',
  'Select Indicator Speed': 'Select Indicator Speed',
  'Select Isolate Color': 'Select Isolate Color',
  'Select Layering Mode': 'Select Layering Mode',
  'Select Management Option': 'Select Management Option',
  'Select Map Theme': 'Select Map Theme',
  'Select Map': 'Select Map',
  'Select Maps to Layer': 'Select Maps to Layer',
  'Select Marker Color': 'Select Marker Color',
  'Select Marker Inclusion Criteria': 'Select Marker Inclusion Criteria',
  'Select Marker Opacity': 'Select Marker Opacity',
  'Select Marker Position': 'Select Marker Position',
  'Select Marker Size': 'Select Marker Size',
  'Select Marker Style': 'Select Marker Style',
  'Select Method': 'Select Method',
  'Select More Columns': 'Select More Columns',
  'Select Name Column': 'Select Name Column',
  'Select Numerical Column...': 'Select Numerical Column...',
  'Select Numerical Data': 'Select Numerical Data',
  'Select Privacy Level': 'Select Privacy Level',
  'Select Region': 'Select Region',
  'Select Sample': 'Select Sample',
  'Select Sharing Method': 'Select Sharing Method',
  'Select Source Territory': 'Select Source Territory',
  'Select Source': 'Select Source',
  'Select Spreadsheet': 'Select Spreadsheet',
  'Select Starting Map View': 'Select Starting Map View',
  'Select Target Territory': 'Select Target Territory',
  'Select Task': 'Select Task',
  'Select Team Members to Share With:': 'Select Team Members to Share With:',
  'Select Territory Group': 'Select Territory Group',
  'Select Territory': 'Select Territory',
  'Select User...': 'Select User...',
  'Select a Group Column': 'Select a Group Column',
  'Select a Group': 'Select a Group',
  'Select a Sheet': 'Select a Sheet',
  'Select a Tool': 'Select a Tool',
  'Select a Unique Identifier': 'Select a Unique Identifier',
  'Select a chart type': 'Select a chart type',
  'Select a column': 'Select a column',
  'Select a map from {{mapList}}': 'Select a map from "{{mapList}}" first',
  'Select a map to transfer': 'Select a map to transfer',
  'Select a version you wish to fall back to': 'Select a version you wish to fall back to',
  'Select action': 'Select action',
  'Select another Map': 'Select another Map',
  'Select authentication method': 'Select authentication method',
  'Select column for each matchup': 'Select column for each matchup',
  'Select column source and type': 'Select column source and type',
  'Select data source (of sales people)': 'Select data source (of sales people)',
  'Select the Group that you want to add the selected boundaries to': 'Select the Group that you want to add the selected boundaries to',
  'Select the Security Status for this map below': 'Select the Security Status for this map below',
  'Select the coverage type.': 'Select the coverage type.',
  'Select the drawing tool to enable drawing': 'Select the drawing tool to enable drawing',
  'Select the maps you wish to restore': 'Select the maps you wish to restore',
  'Select tool': 'Select tool',
  'Select...': 'Select...',
  'Selected Filter: {{columnName}}>{{filterAction}}': 'Selected Filter: {{columnName}}>{{filterAction}}',
  'Selected Filter: {{columnName}}>{{filterAction}}>{{text}}': 'Selected Filter: {{columnName}}>{{filterAction}}>{{text}}',
  'Selected Filter: {{columnName}}>{{selectedGroupName}}': 'Selected Filter: {{columnName}}>{{selectedGroupName}}',
  'Selected Group: {{groupName}}': 'Selected Group: {{groupName}}',
  'Selected Proximities': 'Selected Proximities',
  'Selected data is not numerical': 'Selected data is not numerical',
  'Selected:': 'Selected:',
  'Selecting the option <1>Never for this base map</1> will skip all future updates of this base map.': 'Selecting the option <1>Never for this base map</1> will skip all future updates of this base map.',
  'Send Email': 'Send Email',
  'Send Password Reset': 'Send Password Reset',
  'Send to back': 'Send to back',
  'Send to the middle': 'Send to the middle',
  'Set Constraints': 'Set Constraints',
  'Set Initial Map Position': 'Set Initial Map Position',
  'Set Password': 'Set Password',
  'Set Search Criteria': 'Set Search Criteria',
  'Set the info Popup on your Map (optional)': 'Set the info Popup on your Map (optional)',
  'Set the latitude, longitude and map zoom for me.': 'Set the latitude, longitude and map zoom for me.',
  'Settings And Customization': 'Settings & Customization',
  'Share & Collaborate on this Map with Team Members': 'Share & Collaborate on this Map with Team Members',
  'Share Map & Present': 'Share Map & Present',
  'Share Map': 'Share Map',
  'Share Your Map': 'Share Your Map',
  'Share externally <1>(Public map link, password protected, etc)</1>': 'Share externally <1>(Public map link, password protected, etc)</1>',
  'Share the primary default map.': 'Share the primary default map.',
  'Share with your team': 'Share with your team',
  'Share your current view including saved map position, zoom level, filters, grouping, radius circles, routes, etc.': 'Share your current view including saved map position, zoom level, filters, grouping, radius circles, routes, etc.',
  'Shared View': 'Shared View',
  'Shared/Display Maps': 'Shared Maps / Presentational Maps',
  'Sheet Names': 'Sheet Names',
  'Short3Char.Off': 'Off',
  'Short3Char.On': 'On',
  'Show All Boundaries': 'Show All Boundaries',
  'Show All Territories': 'Show All Territories',
  'Show All': 'Show All',
  'Show Items': 'Show Items',
  'Show Markers For Heat Map: {{heatMapName}}': 'Show Markers For Heat Map: {{heatMapName}}',
  'Show Markers For This Group': 'Show Markers For This Group',
  'Show My Location Default': 'Show My Location Default',
  'Show My Location': 'Show My Location',
  'Show Proximity': 'Show Proximity',
  'Show Street View': 'Show Street View',
  'Show This Many Locations': 'Show This Many Locations',
  'Show User Location on Admin': 'Show User Location on Admin',
  'Show empty groups': 'Show empty groups',
  'Show in Filter Menu': 'Show in Filter Menu',
  'Show on Map': 'Show on Map',
  'Show on Public Map': 'Show on Public Map',
  'Show value': 'Show value',
  'Show/Hide Data Columns from Export functionalities': 'Show/Hide Data Columns from Export functionalities',
  'Show/Hide Data from Export': 'Show/Hide Data from Export',
  'Show/Hide Data on Map Marker Popups': 'Show/Hide Data on Map Marker Popups',
  'Show/Hide Data on Marker': 'Show/Hide Data on Marker',
  'Sign Up Here': 'Sign Up Here',
  'Sign Up': 'Sign Up',
  'Sign in here': 'Sign in here',
  'Sign in': 'Sign in',
  'Single Point (A to B)': 'Single Point (A to B)',
  'Single Point Distance Measurement (A to B)': 'Single Point Distance Measurement (A to B)',
  'Skip this update': 'Skip this update',
  'Skip to Match-up map columns': 'Skip to Match-up map columns',
  'Snapshot Settings': 'Snapshot Settings',
  'Soft limit of {{count}} stops reached': 'Soft limit of {{count}} stops reached',
  'Some labels are automatically hidden at this zoom level because they are too small to be visible.': 'Some labels are automatically hidden at this zoom level because they are too small to be visible.',
  'Some maps or snapshots have not been restored': 'Some maps or snapshots have not been restored',
  'Some waypoint inputs are empty. Please fill in all the waypoints.': 'Some waypoint inputs are empty. Please fill in all the waypoints.',
  'Something didn\'t go as planned.': 'Something didn’t go as planned.',
  'Something went wrong, please try again with a different password.': 'Something went wrong, please try again with a different password.',
  'Something went wrong, please try again.': 'Something went wrong, please try again.',
  'Sorry, we encountered a problem removing the map. Please try again later.': 'Sorry, we encountered a problem removing the map. Please try again later.',
  'Sorry, we encountered a problem sending email. Please try again later.': 'Sorry, we encountered a problem sending email. Please try again later.',
  'Specific Group': 'Specific Group',
  'Split Screen': 'Split Screen',
  'Spreadsheet Data Out Of Sync': 'Spreadsheet Data Out Of Sync',
  'Spreadsheet Data': 'Spreadsheet Data',
  'Spreadsheet has no columns': 'Spreadsheet has no columns',
  'Spreadsheet {{suffix}}': 'Spreadsheet {{suffix}}',
  'Stacked Markers w/Pie Charts': 'Stacked Markers w/Pie Charts',
  'Stacked marker metrics': 'Stacked Marker Metrics',
  'Standard markers': 'Standard markers',
  'Standard settings': 'Standard settings',
  'Start Blank': 'Start Blank',
  'Start Drawing': 'Start Drawing',
  'Start Edit Mode': 'Start Edit Mode',
  'Start Selecting Now': 'Start Selecting Now',
  'Start Selection Mode': 'Start Selection Mode',
  'Start your free, no risk, 10 day trial.': 'Start your free, no risk, 10 day trial.',
  'Starting Location': 'Starting Location',
  'Starts With': 'Starts With',
  'State / Province': 'State / Province',
  'Stay connected to base map - in real time': 'Stay connected to base map - in real time',
  'Stay connected to base map - updates need to be approved': 'Stay connected to base map - updates need to be approved',
  'Step {{step}}': 'Step {{step}}',
  'Stop Drawing': 'Stop Drawing',
  'Straight Line (as the crow flies)': 'Straight Line (as the crow flies)',
  'Street View Not Available': 'Street View Not Available',
  'Street View': 'Street View',
  'Street view error': 'Street view error',
  'Street view search returned no images.': 'Street view search returned no images.',
  'Subscription Management Unavailable': 'Subscription Management Unavailable',
  'SubscriptionManagement.CancelOptions': 'Cancel Subscription Options',
  'SubscriptionManagement.CancelOptions.End': 'Cancel at the end of license period (turn off autopay)',
  'SubscriptionManagement.CancelOptions.Now': 'Cancel Now ',
  'SubscriptionManagement.CancelOptions.NowAndDeleteData': 'Cancel Now and Delete my Data',
  'SubscriptionManagement.ErrorMessage.CancellationFailed': 'Something went wrong, please try again. If the problem persists, please contact our customer support.',
  'SubscriptionManagement.LicenseIssue': 'There seems to be an issue with your license payment. Please resolve the issue by clicking the button. Thank you.',
  'SubscriptionManagement.LicenseIssueFriendlyMessage': 'There seems to be an issue with your license payment so unfortunately can’t let you pass. Please resolve the issue by clicking the button below. Then, I can go take a nap. Thank you!',
  'SubscriptionManagement.LicenseIssueFriendlyMessageMember': 'There seems to be an issue with the license, so unfortunately I can’t let you pass. \n Only the Maptive Account Owner can manage the license. To resolve the issue, please contact the account owner. \n Then, I can go take a nap. Thank you!',
  'SubscriptionManagement.LicenseIssueMember': 'There seems to be an issue with your license payment. Only the Maptive Account Owner can manage the license. To resolve the issue, please contact the account owner.',
  'SubscriptionManagement.LicenseIssuePleasePurchase': 'Unfortunately your license has expired so can’t let you pass. Please purchase a new one to continue your mapping. Then, I can go take a nap. Thank you!',
  'SubscriptionManagement.LicenseIssueTrialPleasePurchase': 'I see your trial has expired. So unfortunately can’t let you pass. Please purchase a license to continue mapping. Thank you!',
  'SubscriptionManagement.LicenseState.active': 'Active',
  'SubscriptionManagement.LicenseState.expired': 'Expired',
  'SubscriptionManagement.LicenseState.failedPayPeriod': 'Failed Pay Period',
  'SubscriptionManagement.LicenseState.gracePeriod': 'Grace Period',
  'SubscriptionManagement.LicenseState.renewalCanceled': 'Renewal Canceled',
  'SubscriptionManagement.LicenseType.45_DAYS': '45 Days',
  'SubscriptionManagement.LicenseType.annual': 'Annual',
  'SubscriptionManagement.LicenseType.monthly': 'Monthly',
  'SubscriptionManagement.LicenseType.trial': 'Trial',
  'SubscriptionManagement.ReactivateOptions.Impossible': 'NOTE: You will not be able to reactivate your account, nor your license.',
  'SubscriptionManagement.ReactivateOptions.Possible': 'NOTE: You can reactivate your license up to 10 days after it expires.',
  'SubscriptionManagement.Unavailable': 'Please use the <1>previous</1> version of Maptive to make any subscription related changes, as this functionality is temporarily unavailable in the current version you are using.',
  'Sum (Total)': 'Sum (Total)',
  'Sync 1 Direction:  Google Sheets >>> to >>> Maptive': 'Sync 1 Direction:  Google Sheets >> to >> Maptive',
  'Sync 2 Directions: Google Sheets >> Maptive and Maptive >> Google Sheets': 'Sync 2 Directions: Google Sheets >> Maptive and Maptive >> Google Sheets',
  'Sync Data': 'Sync Data',
  'Sync Google Sheets Method': 'Sync Google Sheets Method',
  'SyntheticBoundary.Note': 'Artificial Zip Code',
  'SyntheticBoundary.Tooltip': 'This is an artificial Zip Code generated to cover an area where there are no official US Zip Codes.',
  'THERE IS NO ACTION TO UNDO': 'THERE IS NO ACTION TO UNDO',
  'TRIAL (<1>upgrade here</1>)': 'TRIAL (<1>upgrade here</1>)',
  'Team Map Share': 'Team Map Share',
  'Team Member': 'Team Member',
  'Team Members': 'Team Members',
  'Territories Out Of Sync': 'Territories Out Of Sync',
  'Territories have been recently changed by {{userName}}. Please refresh your data in order to be able to make changes.': 'Territories have been recently changed by {{userName}}. Please refresh your data in order to be able to make changes.',
  'Territory Drawing': 'Territory Drawing',
  'Territory Editing': 'Territory Editing',
  'Territory Formatting': 'Territory Formatting',
  'Territory Group Name': 'Territory Group Name',
  'Territory Group Remove Error': 'Territory Group Remove Error',
  'Territory Group Settings': 'Territory Group Settings',
  'Territory Group is already loaded': 'Territory Group is already loaded',
  'Territory Group: {{territoryGroupName}}': 'Territory Group: {{territoryGroupName}}',
  'Territory Groups': 'Territory Groups',
  'Territory Selection Tool': 'Territory Selection Tool',
  'Territory Settings': 'Territory Settings',
  'Territory boundary color': 'Territory boundary color',
  'Territory fill color': 'Territory fill color',
  'Territory group copy error': 'There was an error copying your territory group. Please try again.',
  'Territory group used on {{count}} map_one': 'Territory group is used on {{count}} map',
  'Territory group used on {{count}} map_other': 'Territory group is used on {{count}} maps',
  'Territory group was already added to the map': 'Territory group was already added to the map',
  'Territory group was already added': 'Territory group was already added',
  'Territory group was already removed from the map': 'Territory group was already removed from the map',
  'Territory group was already removed': 'Territory group was already removed',
  'Territory group was successfully copied': 'Territory group was successfully copied!',
  'Territory was already removed from the map': 'Territory was already removed from the map',
  'Territory was already removed': 'Territory was already removed',
  'Text Column': 'Text Column',
  'Text Element': 'Text Element',
  'The Driving Distance calculation currently supports a maximum of {{limit}} markers closest to the center.': 'The Driving Distance calculation currently supports a maximum of {{limit}} markers closest to the center.',
  'The color cannot be changed for All Markers while grouping is active.': 'The color cannot be changed for All Markers while grouping is active.',
  'The counts and metrics calculated in this panel might include markers which are not visible on the map.': 'The counts and metrics calculated in this panel might include markers which are not visible on the map.',
  'The custom map url format is invalid.': 'The custom map url format is invalid.',
  'The data that makes up this layered maps has been updated on the following base maps. Please select what you wish to do for each base map.': 'The data that makes up this layered maps has been updated on the following base maps. Please select what you wish to do for each base map.',
  'The data will be appended to the bottom of the existing data set.': 'The data will be appended to the bottom of the existing data set.',
  'The download should start automatically.': 'The download should start automatically.',
  'The entity was not found.': 'The entity was not found.',
  'The following maps will be affected:': 'The following maps will be affected:',
  'The map <1>{{mapName}}</1> is a part of layered maps.': 'The map <1>{{mapName}}</1> is a part of layered maps.',
  'The map you are looking for doesn\'t exist.': 'The map you are looking for doesn’t exist.',
  'The opacity cannot be changed for All Markers while grouping is active.': 'The opacity cannot be changed for All Markers while grouping is active.',
  'The page you are looking for doesn\'t exist.': 'The page you are looking for doesn’t exist.',
  'The parent map was not found.': 'The parent map was not found.',
  'The provided email address could not be verified': 'The provided email address could not be verified',
  'The requested email address is already assigned to another account.': 'The requested email address is already assigned to another account.',
  'The unmapped data below was removed from your imported dataset': 'The unmapped data below was removed from your imported dataset',
  'The verification link was successfully sent to your email address. Please click on the link in the email to continue.': 'The verification link was successfully sent to your email address. Please click on the link in the email to continue.',
  'The {{entityTypeName}} {{entityName}} could not be restored.': 'The {{entityTypeName}} {{entityName}} could not be restored.',
  'Theme Color': 'Theme Color',
  'Theme Style': 'Theme Style',
  'There Was An Error Managing Your Team Member': 'There Was An Error Managing Your Team Member',
  'There are currently no existing Groups on your map.': 'There are currently no existing Groups on your map.',
  'There are no deleted maps': 'There are no deleted maps',
  'There are no markers in the selected area.': 'There are no markers in the selected area.',
  'There are no older versions': 'There are no older versions',
  'There are too many markers in selected area. Please select area with fewer locations and try again.': 'There are too many markers in selected area. Please select area with fewer locations and try again.',
  'There was an error when uploading your file. Please check your connection and try again. If this problem persists please contact our customer support.': 'There was an error when uploading your file. Please check your connection and try again. If this problem persists please contact our customer support.',
  'These columns will be excluded from the layered map': 'These columns will be excluded from the layered map',
  'These columns will be included in the layered map': 'These columns will be included in the layered map',
  'This column association cannot be broken up.': 'This column association cannot be broken up.',
  'This column has over 500 unique values. Please select a different group column and try again.': 'This column has over 500 unique values. Please select a different group column and try again.',
  'This column was disabled for use on presentational maps by Enable/Disable Columns setting': 'This column was disabled for use on presentational maps by the Enable/Disable Columns setting',
  'This combination is already on the list': 'This combination is already included',
  'This feature allows you to set the initial position. To use, position the map exactly the way you want it to look when someone first opens it (zoom level, etc). Then just click this button and select the options you prefer.': 'This feature allows you to set the initial position. To use, position the map exactly the way you want it to look when someone first opens it (zoom level, etc). Then just click this button and select the options you prefer.',
  'This feature is available only when {{ count }} or fewer proximities are selected': 'This feature is available only when {{ count }} or fewer proximities are selected',
  'This field is required': 'This field is required',
  'This functionality is not available for current user.': 'This functionality is not available for current user.',
  'This functionality requires a numerical column. The selected column consists of non-numerical values. To be able to proceed with this column, please make sure it only contains numerical values.': 'This functionality requires a numerical column. The selected column consists of non-numerical values. To be able to proceed with this column, please make sure it only contains numerical values.',
  'This group was created and customized manually.': 'This group was created and customized manually.',
  'This heatmap already exists on the map. Heatmaps can\'t be duplicated.': 'This heatmap already exists on the map. Heatmaps can’t be duplicated.',
  'This map is set to private and can only be accessed when logged in.': 'This map is set to private and can only be accessed when logged in.',
  'This map is used by the following users. A copy of the map will be created for each of them.': 'This map is used by the following users. A copy of the map will be created for each of them.',
  'This may take a minute...': 'This may take a minute...',
  'This option is disabled while "Display Legend Only" is active': 'This option is disabled while "Display Legend Only" is active',
  'This option is disabled while "Map  Tools Menu Open" is inactive.': 'This option is disabled while "Map  Tools Menu Open" is inactive.',
  'This option is disabled while there are no tools set to be visible on a public map': 'This option is disabled while there are no tools set to be visible on a public map',
  'This option is turned off when "Legend Replaces Grouping" is enabled': 'This option is turned off when "Legend Replaces Grouping" is enabled',
  'This pie chart has no data, try clicking another one': 'This pie chart has no data, try clicking another one',
  'This process can take up to <1>{{fromMinutes}}-{{toMinutes}} minutes</1> depending on the queue and complexity of your map.': 'This process can take up to <1>{{fromMinutes}}-{{toMinutes}} minutes</1> depending on the queue and complexity of your map.',
  'This process can take up to {{fromMinutes}}-{{toMinutes}} minutes.': 'This process can take up to {{fromMinutes}}-{{toMinutes}} minutes.',
  'This site is protected by reCAPTCHA and the Google <1>Privacy Policy</1> and <3>Terms of Service</3> apply.': 'This site is protected by reCAPTCHA and the Google <1>Privacy Policy</1> and <3>Terms of Service</3> apply.',
  'To Here': 'To Here',
  'To Homepage': 'To Homepage',
  'To change your email or password please confirm your current password. This way we can provide you with the most security.': 'To change your email or password please confirm your current password. This way we can provide you with the most security.',
  'To delete your data please confirm your current password. This way we can provide you with the most security.': 'To delete your data please confirm your current password. This way we can provide you with the most security.',
  'Toggle theme': 'Toggle theme',
  'Too many locations': 'Too many locations',
  'Topology metric': 'Topology metric',
  'Total Locations: {{locationsCount}}': 'Total Locations: {{locationsCount}}',
  'Total Public Map Views': 'Total Public Map Views',
  'Total Unique Locations Mapped': 'Total Unique Locations Mapped',
  'Total {{entityName}}': 'Total {{entityName}}',
  'Trash Em': 'Trash’Em',
  'Travel Time to Next Stop': 'Travel Time to Next Stop',
  'Travel Time': 'Travel Time',
  'Trial Expired': 'Trial Expired',
  'TrialExportNotice.header': 'Full Data Exports Only Allowed On Paid Accounts',
  'TrialExportNotice.message': 'Full data exports are only allowed on paid accounts. Trial accounts are permitted to export a sample with 5 rows of data for demo evaluation purposes. Click the "Purchase Now" button to get a paid license or you can click the "Export Sample" to see how the export will look and be formatted.',
  'Try Again': 'Try Again',
  'Try a different map or see all available maps by clicking <1>here</1>.': 'Try a different map or see all available maps by clicking <1>here</1>.',
  'Turn on location finder to enable this option': 'Turn on location finder to enable this option',
  'Turn on location list to enable this option': 'Turn on location list to enable this option',
  'Two Factor Authentication': 'Two Factor Authentication',
  'Type New Marker Information': 'Type New Marker Information',
  'Type your label here': 'Type your label here',
  'UPDATES: in real time': 'UPDATES: in real time',
  'UPDATES: need to be approved': 'UPDATES: need to be approved',
  'US Counties': 'US Counties',
  'US States with Alaska and Hawaii': 'US States with Alaska and Hawaii',
  'US States': 'US States',
  'US Zip Codes with Alaska and Hawaii': 'US Zip Codes with Alaska and Hawaii',
  'US Zip Codes': 'US Zip Codes',
  'Un-/Merging columns': 'Un-/Merging columns',
  'Unable To Create Your Map': 'Unable To Create Your Map',
  'Unable To Find Your Location': 'Unable To Find Your Location',
  'Unable to Synchronize Google Sheet Data': 'Unable to Synchronize Google Sheet Data',
  'Unable to find location': 'Unable to find location',
  'Unable to geocode address': 'Unable to geocode address',
  'Unable to get data from Google Sheets, please try again.': 'Unable to get data from Google Sheets, please try again.',
  'Unable to log in. Please check your internet connection and try again.': 'Unable to log in. Please check your internet connection and try again.',
  'Unable to match Google and Maptive spreadsheet columns.': 'Unable to match Google and Maptive spreadsheet columns.',
  'UnblockAccount.inProgress': 'Unblocking Your Account...',
  'UnblockAccount.linkInvalid': 'The Unblock Account link has already been used or is invalid.',
  'UnblockAccount.success': 'Your account was successfully unblocked.',
  'Uncluster Below': 'Uncluster Below',
  'Undo Map/Map View': 'Undo Map/Map View',
  'Undo Previous Actions': 'Undo Previous Actions',
  'Undo Remove Grouping': 'Undo Remove Grouping',
  'Undo Remove Heatmap': 'Undo Remove Heatmap',
  'Undo Remove Proximity': 'Undo Remove Proximity',
  'Undo Remove Route': 'Undo Remove Route',
  'Unique identifier check OK': 'The selected column(s) form(s) a unique identifier.',
  'Unique identifier check not OK': 'The selected column(s) do(es) not identify your rows sufficiently. Please select a different combination. You can also add a column with unique values to your data.',
  'United States': 'United States',
  'Unknown Location': 'Unknown Location',
  'Unknown column': 'Unknown column',
  'Unlink From Other Tools': 'Unlink From Other Tools',
  'Unnamed columns detected': 'Unnamed columns detected',
  'Unselect All': 'Unselect All',
  'Unstack Markers': 'Unstack Markers',
  'Untitled group': 'Untitled group',
  'Untitled territory': 'Untitled territory',
  'Update Boundary': 'Update Boundary',
  'Upgrade Subscription': 'Upgrade Subscription',
  'Upload / select additional images': 'Upload / select additional images',
  'Upload Custom Marker': 'Upload Custom Marker',
  'Upload Images': 'Upload Images',
  'Upload Markers': 'Upload Markers',
  'Upload New Image': 'Upload New Image',
  'Upload an image': 'Upload an image',
  'Uploading File Problem': 'Uploading File Problem',
  'Use current map view to set the starting map position': 'Use current map view to set the starting map position',
  'Use unique columns for each matchup': 'Use unique columns for each matchup',
  'User <1>{{userName}}</1> has changed the following recently:': 'User <1>{{userName}}</1> has changed the following recently:',
  'User Limit: {{limitsUsed}} of {{limitsTotal}}': 'User Limit: {{limitsUsed}} of {{limitsTotal}}',
  'User with provided email cannot be invited': 'User with provided email cannot be invited',
  'UserImpersonation.Ended': 'Impersonation has ended',
  'UserImpersonation.Expired': 'Impersonation has expired',
  'UserImpersonation.LoggedBackToPreviousUser': 'You have been logged back to the original user account.',
  'UserImpersonation.LoggedBackToPreviousUserTitle': 'User Impersonation Session ended successfully',
  'UserImpersonation.RefreshRequired': 'Since the impersonation session changed, please refresh the page to see the correct data.',
  'UserImpersonation.Start.Failed': 'Failed to start the user impersonalisation session. The request might have expired, please try again.',
  'UserImpersonation.Start.Loading': 'Loading User Impersonalisation Sesssion',
  'UserImpersonation.Start.Note': 'Note: to finish impersonalisation session, just log out.',
  'UserImpersonation.Start.Success': 'Success! Redirecting',
  'UserImpersonation.Started': 'Impersonation has started',
  'Users using map {{mapName}}': 'Users using map {{mapName}}',
  'Validating Request': 'Validating Request',
  'Value Ranges': 'Value Ranges',
  'Verifying the provided email address is in progress': 'Verifying the provided email address  is in progress',
  'View Only': 'View Only',
  'View our sample excel <1>Spreadsheet Template</1>.': 'View our sample excel <1>Spreadsheet Template</1>.',
  'Warning: Performance impact': 'Warning: Performance impact',
  'Water Color': 'Water Color',
  'We could not find any route for your locations.': 'We could not find any route for your locations.',
  'We encountered an error loading this page. Try reloading it - if it doesn\'t work,<1 />try again later or contact customer support.': 'We encountered an error loading this page. Try reloading it - if it doesn’t work,<1 />try again later or contact customer support.',
  'We encountered an error loading this page. Try reloading it - if this doesn\'t work, try again later or contact customer support.': 'We encountered an error loading this page. Try reloading it - if this doesn’t work, try again later or contact customer support.',
  'We encountered an error, please refresh your page and try accessing the payment portal again. If the problem persists, please contact our Customer Support': 'We encountered an error, please refresh your page and try accessing the payment portal again. If the problem persists, please contact our Customer Support',
  'We have observed that one or more of your proximities contain over {{limit}} markers. The Driving Distance calculation currently supports a maximum of {{limit}} markers. Would you like to proceed with exporting up to {{limit}} markers per proximity, or would you prefer to cancel the export?': 'We have observed that one or more of your proximities contain over {{limit}} markers. The Driving Distance calculation currently supports a maximum of {{limit}} markers. Would you like to proceed with exporting up to {{limit}} markers per proximity, or would you prefer to cancel the export?',
  'We were not able to find your location. Please check your browser settings and try again.': 'We were not able to find your location. Please check your browser settings and try again.',
  'We were not able to find your location. Please check your browser settings.': 'We were not able to find your location. Please check your browser settings.',
  'We were unable to create a layered map for you. Please try again. If this problem persists, please contact the customer support.': 'We were unable to create a layered map for you. Please try again. If this problem persists, please contact the customer support.',
  'We were unable to edit a layered map for you. Please try again. If this problem persists, please contact the customer support.': 'We were unable to edit a layered map for you. Please try again. If this problem persists, please contact the customer support.',
  'We were unable to export your image. Please try again. If this problem persists, please contact the customer support': 'We were unable to export your image. Please try again. If this problem persists, please contact the customer support',
  'We were unable to find locations of some of your waypoints.': 'We were unable to find locations of some of your waypoints.',
  'We were unable to proceed with uploading your files. Please fix these issues and try again': 'We were unable to proceed with uploading your files. Please fix these issues and try again',
  'We were unable to re-send the verification email, please try again. If this problem persists please contact our customer support.': 'We were unable to re-send the verification email, please try again. If this problem persists please contact our customer support.',
  'Website URL Column': 'Website URL Column',
  'Welcome {{invitee}}! You have been invited to join Maptive by {{client}}. Do you wish to accept the invitation and sign up?': 'Welcome {{invitee}}! You have been invited to join Maptive by {{client}}. Do you wish to accept the invitation and sign up?',
  'Welcome {{invitee}}! You have been invited to join Maptive by {{client}}. Please create a password to continue.': 'Welcome {{invitee}}! You have been invited to join Maptive by {{client}}. Please create a password to continue.',
  'Welcome {{invitee}}! You have been invited to join Maptive. Do you wish to accept the invitation and sign up?': 'Welcome {{invitee}}! You have been invited to join Maptive. Do you wish to accept the invitation and sign up?',
  'Welcome {{invitee}}! You have been invited to join Maptive. Please create a password to continue.': 'Welcome {{invitee}}! You have been invited to join Maptive. Please create a password to continue.',
  'What would you like to do with the markers in the lasso area you have just drawn': 'What would you like to do with the markers in the lasso area you have just drawn',
  'What would you like to do with the selected boundaries?': 'What would you like to do with the selected boundaries?',
  'What would you like to do with the {{method}} areas?': 'What would you like to do with the {{method}} areas?',
  'When enabled, clicking on Points of Interest (POIs) like restaurants, shops, or landmarks will display additional information provided by Google Maps. Disable this if you want to prevent users from interacting with these built-in map icons.': 'When enabled, clicking on Points of Interest (POIs) like restaurants, shops, or landmarks will display additional information provided by Google Maps. Disable this if you want to prevent users from interacting with these built-in map icons.',
  'Woof! Im Smudge, Maptives friendly guard dog.': 'Woof! I’m Smudge, Maptive’s friendly guard dog.',
  'Would you like Maptive to show your location on the map?': 'Would you like Maptive to show your location on the map?',
  'Wrong password': 'Wrong password',
  'Yes, continue': 'Yes, continue',
  'You Have Too Many Rows In Your Google Sheets File': 'You Have Too Many Rows In Your Google Sheets File',
  'You are about to delete a file that is currently used on map:_one': 'You are about to delete a file that is currently used on map:',
  'You are about to delete a file that is currently used on map:_other': 'You are about to delete a file that is currently used on maps:',
  'You are about to delete a fill group. This action is irreversible. Do you want to proceed?': 'You are about to delete a fill group. This action is irreversible. Do you want to proceed?',
  'You are about to delete a proximity. You can undo this action any time with the Undo modal. Do you want to proceed?': 'You are about to delete a proximity. You can undo this action any time with the Undo modal. Do you want to proceed?',
  'You are about to delete a territory. This action is irreversible. Do you want to proceed?': 'You are about to delete a territory. This action is irreversible. Do you want to proceed?',
  'You are about to delete all drawings from the map. This action is irreversible. Do you wish to continue?': 'You are about to delete all drawings from the map. This action is irreversible. Do you wish to continue?',
  'You are about to delete all {{drawingsType}} from the map. This action is irreversible. Do you wish to continue?': 'You are about to delete all {{drawingsType}} from the map. This action is irreversible. Do you wish to continue?',
  'You are about to delete everything. This action is irreversible. Do you wish to proceed?': 'You are about to delete everything. This action is irreversible. Do you wish to proceed?',
  'You are about to delete this element. This action is irreversible. Do you wish to proceed?': 'You are about to delete this element. This action is irreversible. Do you wish to proceed?',
  'You are about to delete this element. You can undo this action any time with the Undo modal.': 'You are about to delete this element. You can undo this action any time with the Undo modal.',
  'You are about to delete this map view. You can undo this action any time with the Undo modal.': 'You are about to delete this map view. You can undo this action any time with the Undo modal.  Do you wish to proceed?',
  'You are about to delete this member. Once deleted, a member cannot be re-added. Do you wish to proceed?': 'You are about to delete this member. Once deleted, a member cannot be re-added. Do you wish to proceed?',
  'You are about to delete this {{drawingElement}} from the map. This action is irreversible. Do you wish to continue?': 'You are about to delete this {{drawingElement}} from the map. This action is irreversible. Do you wish to continue?',
  'You are about to disconnect a map...': 'You are about to disconnect a map. This action is irreversible. Do you wish to proceed?',
  'You are about to remove a member from your map. You can re-add the member any time. Do you wish to proceed?': 'You are about to remove a member from your map. You can re-add the member any time. Do you wish to proceed?',
  'You are about to remove a {{entity}} from the map. Do you want to proceed?': 'You are about to remove a {{entity}} from the map. Do you want to proceed?',
  'You are about to remove this element from the map. Do you wish to proceed?': 'You are about to remove this element from the map. Do you wish to proceed?',
  'You are currently logged in as active user. To access this page, you need to sign out first.': 'You are currently logged in as active user. To access this page, you need to sign out first.',
  'You are currently logged in under <1>{{GoogleAccount}}</1>.': 'You are currently logged in under <1>{{GoogleAccount}}</1>.',
  'You are going to <1>delete</1> {{count}} layered maps. The layered maps will get deleted but no data is lost since they are still available in the single base maps.': 'You are going to <1>delete</1> {{count}} layered maps. The layered maps will get deleted but no data is lost since they are still available in the single base maps.',
  'You are going to <1>delete</1> {{count}} layered maps. The layered maps will get deleted but no data is lost since they are still available in the single base maps._one': 'You are going to <1>delete</1> {{count}} layered map. The layered maps will get deleted but no data is lost since they are still available in the single base maps.',
  'You are going to <1>disconnect</1> {{count}} layered maps. This means the layered maps will look the same, but the source of the markers will be different.': 'You are going to <1>disconnect</1> {{count}} layered maps. This means the layered maps will look the same, but the source of the markers will be different.',
  'You are going to <1>disconnect</1> {{count}} layered maps. This means the layered maps will look the same, but the source of the markers will be different._one': 'You are going to <1>disconnect</1> {{count}} layered map. This means the layered maps will look the same, but the source of the markers will be different.',
  'You are in offline mode, your progress can\'t be saved.': 'You are in offline mode, your progress can’t be saved.',
  'You are logged in. <1>Log out.</1>': 'You are logged in. <1>Log out.</1>',
  'You can also postpone your decision about a base map until later, or the next time the base map is updated.': 'You can also postpone your decision about a base map until later, or the next time the base map is updated.',
  'You can either delete one of your existing maps or contact <1>support</1> to purchase additional maps. Thanks for using Maptive.': 'You can either delete one of your existing maps or contact <1>Maptive Support</1> to purchase additional maps. Thanks for using Maptive.',
  'You can now drag and drop one of your map markers to a new location on the map. It will remain in the new location permanently if you save the change.': 'You can now drag and drop one of your map markers to a new location on the map. It will remain in the new location permanently if you save the change.',
  'You can now drag and drop one of your marker labels to a new position. It will remain in the new position permanently if you save the change.': 'You can now drag and drop one of your marker labels to a new position. It will remain in the new position permanently if you save the change.',
  'You can now safely close this page': 'You can now safely close this page',
  'You cannot edit territories when an alternative fill is active': 'You cannot edit territories when an alternative fill is active',
  'You must first verify your email address before you log in.': 'You must first verify your email address before you log in.',
  'You purchased a brand new subscription': 'You purchased a brand new subscription',
  'You\'re almost there! Simply match the corresponding columns and then decide on a name for your layered column. You can either use one of the original column names or come up with a brand-new one.': 'You’re almost there! Simply match the corresponding columns and then decide on a name for your layered column. You can either use one of the original column names or come up with a brand-new one.',
  'Your Current Location': 'Your Current Location',
  'Your Current View Map': 'Your Current View Map',
  'Your Data': 'Your Data',
  'Your Map Limit Has Been Reached.': 'Your Map Limit Has Been Reached.',
  'Your account has been created. Please login to continue.': 'Your account has been created. Please login to continue.',
  'Your account information has been updated successfully.': 'Your account information has been updated successfully.',
  'Your account was created successfully!': 'Your account was created successfully!',
  'Your current license does not permit additional Driving Distance exports. To increase your quota, please contact <1>customer support</1>.': 'Your current license does not permit additional Driving Distance exports. To increase your quota, please contact <1>customer support</1>.',
  'Your current license does not permit additional Driving Distance exports. To increase your quota, please upgrade your license.': 'Your current license does not permit additional Driving Distance exports. To increase your quota, please upgrade your license.',
  'Your data were never fully imported to the map.': 'Your data were never fully imported to the map.',
  'Your email address has been successfully changed.': 'Your email address has been successfully changed.',
  'Your email address was verified successfully': 'Your email address was verified successfully',
  'Your image gallery is empty.': 'Your image gallery is empty.',
  'Your maps have been transferred successfully': 'Your maps have been transferred successfully',
  'Your password was reset successfully': 'Your password was reset successfully',
  'Your saved map views will be listed here': 'Your saved map views will be listed here',
  'Your saved proximities will be listed here': 'Your saved proximities will be listed here',
  'Your visible locations will be listed here': 'Your visible locations will be listed here',
  'Zip / Postal Code': 'Zip / Postal Code',
  'Zoom To Proximity': 'Zoom To Proximity',
  'Zoom to Location': 'Zoom to Location',
  'Zoom to Route': 'Zoom to Route',
  'Zoom to Territory': 'Zoom to Territory',
  'Zooming required to see boundary': 'Zooming required to see boundary',
  'aboutUs.attributionNoticesHeader': 'The following sets forth attribution notices for third party software that may be contained in portions of the Maptive frontend product.',
  'aboutUs.termsOfService': 'The use of this product is subject to the terms of the <1>Maptive Terms of Service</1>, unless otherwise specified therein.',
  'aboutUs.title': 'About Maptive',
  'accessInvitations.errors.403.text': 'The invitation you selected is no longer available. This may be because it has already been accepted, withdrawn by the sender, or previously declined.',
  'accessInvitations.map.inviteText': '{{userName}} has invited you to collaborate on their map "{{mapName}}" with {{mapSharePermission}} access.',
  'administrative.country': 'Country',
  'administrative.landParcel': 'Land parcel',
  'administrative.locality': 'Locality',
  'administrative.neighborhood': 'Neighborhood',
  'administrative.province': 'Province',
  'another user': 'another user',
  'available Public Map Views': 'available Public Map Views',
  'available {{entityName}}': 'available {{entityName}}',
  'boundary-territory-name-group-tie': 'GROUP TIE',
  'boundary-territory-name-no-data': 'NO DATA PRESENT',
  'boundary-territory-name-no-locations': 'NO LOCATIONS',
  'boundary-territory-name-no-numerical': 'NO NUMERICAL',
  'boundary-territory-name-range': '{{valueFrom}}+ to {{valueTo}}',
  'boundaryAI.+ Add New Map with sales people': '+ Add New Map with sales people',
  'boundaryAI.A subset of': 'A subset of',
  'boundaryAI.Add additional constraints': 'Add additional constraints',
  'boundaryAI.Apply Current Filter On Data': 'Apply active filters from filter tool before territory creation',
  'boundaryAI.Building Block Type': 'Building Block Type',
  'boundaryAI.Choose Subset Selection Method': 'Choose Subset Selection Method',
  'boundaryAI.Constraints.Error.MaximumLowerThan{{LowestMaximum}}': 'The maximum value is lower than the lowest allowed value.',
  'boundaryAI.Constraints.Error.MinimumHigherThan{{HighestMinimum}}': 'The minimum value is higher than the highest allowed value.',
  'boundaryAI.From Data': 'From Data',
  'boundaryAI.Generate Territory': 'Generate Territory',
  'boundaryAI.Hover Over On Map': 'Hover Over On Map',
  'boundaryAI.Include all {{blocks}} for the territory generation': 'Include all {{blocks}} for the territory generation',
  'boundaryAI.Include only a subset of {{blocks}} for the territory generation': 'Include only a subset of {{blocks}} for the territory generation',
  'boundaryAI.Lasso Group on Map': 'Lasso Group on Map',
  'boundaryAI.Name Your territory group': 'Name Your territory group',
  'boundaryAI.Number of territories': 'Number of territories',
  'boundaryAI.On Map': 'On Map',
  'boundaryAI.Optimize territories using constraints': 'Optimize territories using constraints',
  'boundaryAI.Override original territory': 'Override original territory',
  'boundaryAI.Processing your territories...': 'Processing your territories...',
  'boundaryAI.Respect Sales Reps Locations': 'Respect Sales Reps Locations',
  'boundaryAI.Respect previous Territories': 'Respect previous Territories',
  'boundaryAI.Respecting the previous territory is...': 'Respecting the previous territory is prioritized over this option.',
  'boundaryAI.Select Building Block Type': 'Select Building Block Type',
  'boundaryAI.Select Data Column with Sales Rep...': 'Select Data Column with Sales Rep Name',
  'boundaryAI.Select Individually on Map': 'Select Individually on Map',
  'boundaryAI.Select Map Containing Sales Rep Locations': 'Select Map Containing Sales Rep Locations',
  'boundaryAI.Select Map with Sales People...': 'Select Map with Sales People...',
  'boundaryAI.Select Previously Saved Territory': 'Select Previously Saved Territory',
  'boundaryAI.Select Territory Creation Method': 'Select Territory Creation Method',
  'boundaryAI.Select territory creation variables': 'Select Territory Creation Variables',
  'boundaryAI.Select the subset area on map': 'Select the subset area on map',
  'boundaryAI.Summary data for setting additional constraints': 'Summary data for setting additional constraints',
  'boundaryAI.Summary data for territory optimizing with weights': 'Summary data for territory optimizing with weights',
  'boundaryAI.Territory Group Name': 'Territory Group Name',
  'boundaryAI.The actual number of territories might be...': 'The actual number of territories might be different. This way, we increase the chance of the constraints being met.',
  'boundaryAI.We changed your requested number of territories to match the number of sales reps.': 'We changed your requested number of territories to match the number of sales reps.',
  'boundaryAI.We changed your requested number of territories to match the number of the previous territories.': 'We changed your requested number of territories to match the number of the previous territories.',
  'boundaryAI.Will adjust the number to the Previously Saved Territory': 'Will adjust the number to the Previously Saved Territory',
  'boundaryAI.Will adjust to the number of Sales Reps': 'Will adjust to the number of Sales Reps',
  'boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"': 'You cannot use "{{disabled}}" together with "{{enabled}}"',
  'boundaryAI.building.waitTimeInfo': 'Territories are being built.  Depending on data complexity, times range from 1-15 minutes',
  'boundaryAI.{{count}} blocks selected': '{{count}} blocks selected',
  'boundaryAI.{{count}} blocks selected_one': '{{count}} block selected.',
  'boundaryEdit.complexBoundaryReason.holes': 'Drag edit is not supported for territories with holes. Please use the draw editing method.',
  'boundaryEdit.complexBoundaryReason.outlineCount': 'Drag edit is not supported for large territories with too many vertices (territory plot points). Please use the draw editing method.',
  'census.Age_10to14': 'Age 10 to 14',
  'census.Age_15to19': 'Age 15 to 19',
  'census.Age_16andOver': 'Age 16 and Over',
  'census.Age_18andOver': 'Age 18 and Over',
  'census.Age_20to24': 'Age 20 to 24',
  'census.Age_21andOver': 'Age 21 and Over',
  'census.Age_25to29': 'Age 25 to 29',
  'census.Age_30to34': 'Age 30 to 34',
  'census.Age_35to39': 'Age 35 to 39',
  'census.Age_40to44': 'Age 40 to 44',
  'census.Age_45to49': 'Age 45 to 49',
  'census.Age_50to54': 'Age 50 to 54',
  'census.Age_55to59': 'Age 55 to 59',
  'census.Age_5to9': 'Age 5 to 9',
  'census.Age_60to64': 'Age 60 to 64',
  'census.Age_62andOver': 'Age 62 and Over',
  'census.Age_65andOver': 'Age 65 and Over',
  'census.Age_65to69': 'Age 65 to 69',
  'census.Age_70to74': 'Age 70 to 74',
  'census.Age_75to79': 'Age 75 to 79',
  'census.Age_80to84': 'Age 80 to 84',
  'census.Age_85andOver': 'Age 85 and Over',
  'census.Age_Under5': 'Age Under 5',
  'census.Area': 'Area',
  'census.FemalePopulation': 'Female Population',
  'census.MalePopulation': 'Male Population',
  'census.Population': 'Population',
  'columnSettings.header.spreadsheetReplace': 'Some of the unmapped columns are associated with settings and tools.',
  'columnSettings.warnSettingsLost.googleSync': 'Please note that any previous settings linked to columns will be lost. You can review them below. If changes are unintended, please revise and try again.',
  'columnSettings.warnSettingsLost.spreadsheetReplace': 'Please note that any previous settings linked to old unmapped columns will be lost. You can review them below. If changes are unintended, please revise and try again.',
  'columnSettingsModal.title': 'Confirm Deletion of Map Settings',
  'contactUs.availability.email': 'We’ll get back to you within 24 hours',
  'contactUs.availability.hours': '{{fromHour}}AM - {{toHour}}PM PT',
  'contactUs.call.title': 'Call Us',
  'contactUs.chat.title': 'Chat with Us',
  'contactUs.email.title': 'Email Us',
  'contactUs.title': 'Contact Us',
  'contactUs.topic.bugReport': 'Report a Bug',
  'contactUs.topic.generalSupport': 'General Support',
  'contactUs.topic.salesQuestions': 'Sales Questions',
  'could not be found using the Google API.': 'could not be found using the Google API.',
  'custom-boundary-name-numbered': 'Territory {{position}}',
  'deleteConfirmation.dataRows': 'Are you sure you want to delete the selected rows? This action can be undone in the {{modalName}} modal under the {{tabName}} tab. Do you want to proceed?',
  'deleteConfirmation.lassoMarkers': 'Are you sure you want to delete the {{count}} markers contained in this area? This action can be undone in the {{modalName}} modal under the {{tabName}} tab. Do you want to proceed?',
  'deleteConfirmation.lassoMarkers_one': 'Are you sure you want to delete the {{count}} marker contained in this area? This action can be undone in the {{modalName}} modal under the {{tabName}} tab. Do you want to proceed?',
  'deleteConfirmation.singleMarker': 'Are you sure you want to delete this marker? This action can be undone in the {{modalName}} modal under the {{tabName}} tab. Do you want to proceed?',
  'directionsOverSoftLimitConfirmation{{count}}': 'Please Note: Directions with more than {{count}} stops may not return the most optimal route, since approximations are used when calculating the route. You can reorder the stops after the route is completed.',
  'directionsTool.emptyStateDescription': 'No Saved Routes / Directions',
  'directionsTool.emptyStateTitle': 'Your saved routes/directions will be listed here',
  'directionsTool.inputPlaceholder': 'Enter Address',
  'directionsTool.listing.header': 'Routes / Directions',
  'drawingTool.arrowToolDescription': 'Click, hold and drag to draw an arrow.',
  'drawingTool.arrowToolDescriptionAdditional': 'Release the click to complete.',
  'drawingTool.arrowToolLabel': 'Draw Arrow',
  'drawingTool.calloutToolDescription': 'Click on the map to draw a callout and enter the text.',
  'drawingTool.calloutToolLabel': 'Draw Callout',
  'drawingTool.circleToolDescription': 'Click and drag the mouse to draw a circle.',
  'drawingTool.circleToolLabel': 'Draw Circle',
  'drawingTool.iconMarkerToolDescription': 'Choose an icon from the list below and then click the map to place your icon marker.',
  'drawingTool.iconMarkerToolLabel': 'Draw Icon Marker',
  'drawingTool.imageToolDescription': 'Choose an image from your image library or upload a new one and then click the map to place it.',
  'drawingTool.imageToolLabel': 'Upload / Display Image',
  'drawingTool.labelToolDescription': 'Click on the map to place and write your label.',
  'drawingTool.labelToolLabel': 'Draw Label',
  'drawingTool.managementToolDescription': 'Elements you draw on the map can be viewed and managed here',
  'drawingTool.numberToolDescription': 'Click on the map to draw a number.',
  'drawingTool.numberToolDescriptionAdditional': 'Click again to draw the next consecutive number.',
  'drawingTool.numberToolLabel': 'Draw Number',
  'drawingTool.polygonToolDescription': 'Click on the map to start drawing a polygon.',
  'drawingTool.polygonToolLabel': 'Draw Polygon',
  'drawingTool.polylineToolDescription': 'Click once on the map to start drawing a line and double click to end the line.',
  'drawingTool.polylineToolLabel': 'Draw Polyline',
  'drawingTool.rectangleToolDescription': 'Click and drag the mouse to draw a square / rectangle.',
  'drawingTool.rectangleToolLabel': 'Draw Rectangle',
  'drawingTool.textToolDescription': 'Click on the map to place and write your text.',
  'drawingTool.textToolLabel': 'Draw Text',
  'exportPresentationalMapData.activateTools.caption': 'Activate Data Export Features On Shared / Public Maps',
  'exportPresentationalMapData.activateTools.desc': 'Select the export features that you want to authorize and activate below.',
  'exportPresentationalMapData.activateTools.warning': 'Pay special attention to the fact that ANY feature activated below will allow your map data to be accessible and exportable by any visitor with access to your public map. DO NOT activate this feature if your map contains sensitive or proprietary data that you do not want made available to the public.',
  'exportPresentationalMapData.contactUs.caption': 'AUTHORIZE EXPORTING of data from PUBLIC / SHARED Maps',
  'exportPresentationalMapData.contactUs.email.subject{{CLIENT_EMAIL}}': 'Authorize Export Data on my Maptive Account {{CLIENT_EMAIL}}',
  'exportPresentationalMapData.contactUs.text': 'Due to the sensitive nature of exporting data from a private account via publicly shared maps, you must first contact Maptive Support to authorize us in writing to activate this feature on your account. Click the "Contact Support" button below to begin the process.',
  'exportProximity.limitResults.inclusion.allMarkersDisabledWhenClosestSelected': 'This option is not available when returning only the closest markers to the center.',
  'filter.settings.note.on.off': '*Turning the filter tool "off" for any of your data will remove/hide it from the filter tool. It will still be visible on the map.',
  'filterTool.date.includeNotADate': 'Include Rows Without a Date',
  'filterTool.date.includeNotADate.disabledMessage.fromToNotAvailable': 'Set From or To date to enable this functionality',
  'geometry.fill': 'Fill',
  'geometry.stroke': 'Stroke',
  'googleSheets.create.selectOneSheet': 'Your selected spreadsheet contains multiple sheets. Please select one to continue.',
  'googleSheets.create.unable': 'Unable to create a map from your Google Sheet. Please try again, if this problem persists please contact our customer support.',
  'googleSheets.dataSourceError.empty': 'The selected spreadsheet is empty. Please add data to your spreadsheet and try again.',
  'googleSheets.dataSourceError.general': 'We were unable to update your spreadsheet. Please try again, if this problem persists please contact our customer support.',
  'googleSheets.dataSourceError.noHeader': 'The Google Sheet you selected sheet has a missing header. Please add a header as the first line of the sheet file and try again.',
  'googleSheets.dataSourceError.notFound': 'We cannot access the spreadsheet that was used to create this map. Please make sure the spreadsheet is available at its original location and try again.',
  'googleSheets.dataSourceError.title': 'There was an error fetching your spreadsheet data',
  'googleSheets.explanation.for.bothways.sync.direction': 'By clicking the sync button, you can also propagate changes from your spreadsheet data to your Google Sheets.',
  'googleSheets.explanation.for.oneway.sync.direction': 'Select this option if you want to maintain all of your data on your Google Spreadsheet. Any updates will only flow one direction from your Google Spreadsheet to Maptive.',
  'googleSheets.googleAccUsedByAnotherClient.errorText': 'Please use a different Google Account or log in to your other Maptive account and use the Sync Data Action on the DATA page of any map created from a Google Sheet to log out. If you have any questions, please contact our Customer Support.',
  'googleSheets.googleAccUsedByAnotherClient.errorTitle': 'The Google Account you are using to connect to your Google Sheets is used by another Maptive account.',
  'googleSheets.googleAccUsedByAnotherClient.title': 'Google Account Used By Another Maptive Account',
  'googleSheets.googleSheet.columnsChanged.caption': 'Structure of Google Spreadsheet has changed',
  'googleSheets.googleSheet.columnsChanged.description': 'The structure of your Google Spreadsheet has changed and its columns no longer match your Maptive spreadsheet columns.',
  'googleSheets.googleSheet.columnsChanged.overwritten': 'The data in your Maptive Spreadsheet is about to get replaced with the Google Sheet data. Do you wish to proceed?',
  'googleSheets.googleSpreadsheetNotFound.caption': 'Unable to Get Data from Google Sheets',
  'googleSheets.googleSpreadsheetNotFound.message{{SPREADSHEET_ID}}': 'Oops, we were unable to fetch data from your Google Sheet file. Please verify that document with the id "{{SPREADSHEET_ID}}" exists on your Google Drive and try again.',
  'googleSheets.note.for.bothways.sync.direction': 'This option requires your data to contain a column with unique identifiers, e.g. the column named ID contains a GUID for each row.',
  'googleSheets.permissionsNotGiven.errorText': 'In order to gain access to your Google Sheets, it is essential that we receive the appropriate permissions. This can be accomplished by confirming that all permission checkboxes are marked during the Google authorization process. If the permissions were not granted by mistake, this can be corrected by reinitiating the process of creating or syncing the Google Sheet, during which you will be asked to grant the permissions again.',
  'googleSheets.permissionsNotGiven.errorTitle': 'We have not received the necessary permissions to access your Google Sheets data.',
  'googleSheets.permissionsNotGiven.title': 'Permissions has not been granted.',
  'googleSheets.sync.duplicates.caption': 'Sync Failed: Duplicated Rows Detected',
  'googleSheets.sync.duplicates.description': 'We have detected multiple rows with the same unique identifier. To be able to complete the sync operation, please fix your {{maptiveOrGoogle}} spreadsheet data so they contain only one row per each of the unique identifiers listed below.',
  'googleSheets.sync.duplicates.resolution.dataPage': 'You will now be redirected back to your home page where you can fix your data. When you are ready you can start the sync process again.',
  'googleSheets.sync.duplicates.resolution.googleSheet': 'After you have adjusted your Google Sheet data, please click Retry.',
  'googleSheets.sync.lostConnectionToGoogleSheet.instructions{{spreadsheetName}}': 'Please, re-select your Google Sheet named "{{spreadsheetName}}" on your Google Drive to reconnect.',
  'googleSheets.sync.lostConnectionToGoogleSheet.warning': 'We are unable to establish connection to your Google Sheet.',
  'googleSheets.sync.notLoggedIn': 'If you are not logged in already, you will be prompted to sign into your Google account.',
  'googleSheets.sync.overrideFromGoogle.desc': 'Your data will be replaced by the Google Sheet.',
  'googleSheets.sync.overrideFromGoogle.note': 'NOTE: Any columns in your Maptive spreadsheet which were not added using the \'Add a New Column\' functionality OR are not present in the Google Sheet will be removed',
  'googleSheets.sync.tryAgain.caption': 'Try Two-Way Sync Again: The Google Sheet Data have been fixed',
  'googleSheets.sync.tryAgain.desc': 'To be able to complete a two-way sync, we need to be able to match all your Maptive Spreadsheet columns to your Google Sheet columns. This means that the headers of your spreadsheets need to match, or they need to contain the same amount of columns.',
  'googleSheets.sync.tryAgain.warning': 'Select this option ONLY if you have MANUALLY fixed your Google Sheet columns to match your Maptive spreadsheet columns.',
  'googleSheets.sync.unableToSync': 'We were unable to synchronize your Google Sheet Data. Please make sure that the Google Sheet that was used to create this map is still available and accessible from the same Google Account. If this problem persists, please contact the customer support.',
  'graphicSettings.clusteringForcedNotice': 'To improve responsiveness of your map, we have enabled the "Cluster Dense Markers" functionality. You can always change this settings under Map Settings -> Map Markers / Graphics',
  'groupingTool.invalidColumn.limits{{MAX_GROUP_COUNT}}Explanation': 'Unable to group by the selected column. A valid column for grouping must have more than 1 and less than {{MAX_GROUP_COUNT}} unique values or be numerical.',
  'groupingTool.invalidColumn.limits{{MAX_GROUP_COUNT}}Explanation.replaceColumn': 'Unable to change the grouping column type. A valid column for text grouping must have more than 1 and less than {{MAX_GROUP_COUNT}} unique values.',
  'if it looks like the process is frozen': 'if it looks like the process is frozen',
  'inviteeInvitation.accepted': 'This invitation was already accepted.',
  'inviteeInvitation.existingUserMustLogIn': 'An account for this email address already exists. To accept the invitation, please log in with the correct user account.',
  'inviteeInvitation.expired': 'This invitation is expired.',
  'inviteeInvitation.invitationForExistingAccountNeedTransfer': 'In order to accept {{client}}’s invitation, please contact Maptive Customer Support so you can instruct us on how you want to treat your existing Maptive account.',
  'inviteeInvitation.notMeantForLoggedUser': 'This invitation is not meant for the currently logged-in user. To accept the invitation, please log out first.',
  'inviteeInvitation.rejected': 'This invitation was already rejected.',
  'inviteeInvitation.unknownError': 'There was some unknown error when processing your invitation. Please try clicking the invitation link again or ask for a new invitation. If the problem persists please contact our customer support.',
  'labels.icon': 'Icon',
  'labels.poi.recreation': 'Recreation',
  'labels.text': 'Text',
  'labels.text.fill': 'Text fill',
  'labels.text.stroke': 'Text outline',
  'landscape.humanMade': 'Human-made',
  'landscape.natural': 'Natural',
  'landscape.natural.landcover': 'Land cover',
  'landscape.natural.terrain': 'Terrain',
  'layeredMap.cannotEditBaseMapShared': 'You can’t change base map data on a connected layered map when the base map is shared among multiple users.  Data changes need to be made from the base map containing the data you want to change and that will flow through to the layered map.',
  'layeredMap.cannotEditBaseNoIntersectForColumn': 'This cell in the connected layered map cannot be edited. The layered map column "{{columnName}}" does not correspond to any column in the base map "{{baseMapName}}".',
  'layeredMap.cannotEditMapEditBaseWhenNotRealtime': 'You can’t edit data on a layered map connected to the base-maps with data change approval.',
  'layeredMap.cannotEditShared': 'You can’t change data on a connected layered map which is shared among multiple users.  Data changes need to be made from the base map containing the data you want to change and that will flow through to the layered map.',
  'layeredMap.connected.batchEditNotAllowed': 'Editing multiple spreadsheet values is currently not supported for connected layered maps.',
  'locationListing.maxnumber.exceed.warning': 'To keep your map running fast, the Location List will only display the list of addresses if there are [NUMBER] or less visible on the map.',
  'locationListing.maxnumber.exceed.warning.hint': 'Zoom in on your map to display fewer locations and to activate the Location List.',
  'manual group': 'manual group',
  'manual group; no data': 'manual group; no data',
  'mapListing.emptyStateDescription': 'Your maps will be listed here',
  'mapListing.emptyStateTitle': 'No maps saved to your account',
  'mapListing.emptyStateTitle.withFilter': 'No maps match your search',
  'mapRowSettingsResolution.caption': 'Choose What To Do With Rows Related Settings',
  'mapRowSettingsResolution.option.erase.desc': 'Selecting this option will result in erasing this map’s settings that are connected to your current spreadsheet rows, such as individual marker visuals, marker images, etc.',
  'mapRowSettingsResolution.option.erase.label': 'Erase Map Settings Related to Rows (Locations)',
  'mapRowSettingsResolution.option.uniqueKey.desc': 'Choosing this option allows you to select columns in your new spreadsheet to build a unique key which will be used to map old map settings to new which allows us to preserve them.',
  'mapRowSettingsResolution.option.uniqueKey.label': 'Map Existing Rows to New Rows to Preserve Map Settings',
  'mapRowSettingsResolution.option.uniqueKey.note': 'Only the columns which were mapped to old columns in the previous step can be used.',
  'mapSettingsDisabledInfo.BlockedByClusteringOff': 'This option is disabled while marker clustering is off.',
  'mapSettingsDisabledInfo.Cluster{{N}}DenseMarkers': 'To ensure smooth performance, clustering is enabled when there are more than {{N}} labeled markers on the map.',
  'mapSettingsDisabledInfo.StackedMarkersWithPieCharts': 'This functionality is enabled only when "Unstack Markers" is OFF.',
  'mapSettingsDisabledInfo.UnblockClusteringByUnstack{{unstackName}}': 'We have noticed that your map contains a significant number of stacked markers. Enabling the "{{unstackName}}" functionality in the menu below would allow you to disable clustering.',
  'mapSettingsDisabledInfo.unstackMarkersForced': 'This setting was automatically activated by the {{unclusterBelowN}} functionality.',
  'mapSettingsInfo.ActiveMarkerIndicator': 'Use this feature to switch between different Active Marker Indicators.',
  'mapSettingsInfo.AllowSatelliteView': 'This feature adds an option that lets users of your map view it using Google Satellite View.',
  'mapSettingsInfo.AllowStreetView': 'This feature adds an icon to your map that users can drag and drop to see parts of your map using Google Street View (where coverage is available).',
  'mapSettingsInfo.BoundaryTool': 'This feature deactivates the boundary / territory tool on the presentation mode of your map.',
  'mapSettingsInfo.ClusterDenseMarkers': 'This feature simplifies the viewing of crowded map marker areas by clustering them under one graphic. It also greatly improves the performance of maps with lots of locations by reducing the number of graphics that the software needs to draw on the map.',
  'mapSettingsInfo.ClusterWithPieCharts': 'This feature clusters crowded map marker areas using a pie chart that display the percentage of each group in the cluster.',
  'mapSettingsInfo.CustomizeAllMarkers': 'This feature lets you customize all markers.',
  'mapSettingsInfo.CustomizeMapUrl': 'Customize presentational map URL',
  'mapSettingsInfo.DefaultOpenMapToolPresentationalMap': 'Select the tool to be displayed after the tool menu is opened',
  'mapSettingsInfo.DisableActiveToolPanel': 'Hide the panel at the bottom of the map that displays all of the active tools applied to the map',
  'mapSettingsInfo.DisplayLegendOnly': 'This feature will turn off all of the map tools and only display the legend on the map tools menu',
  'mapSettingsInfo.DisplaySearchBox': 'This feature will display or remove the search button which brings up the search bar in the presentational map mode.',
  'mapSettingsInfo.DisplayTitleAndDescription': 'This feature will display or remove the title and description along the top of your map in presentations mode.',
  'mapSettingsInfo.DistanceCalculator': 'Turning this switch to the OFF position will deactivate the straight-line distance calculator on your shared or public maps.',
  'mapSettingsInfo.ExportingDataFeatures': 'Exporting Data Features',
  'mapSettingsInfo.ExportingDataFeatures.allowForAccountFirst': 'To enable "{{functionalityName}}", please navigate to "{{menuName}}" first, access the "{{subMenuName}}" section to activate this functionality for your account, then return to this menu to proceed.',
  'mapSettingsInfo.ExportingDataFeatures.askAdminToAllow': 'This feature has been restricted by an admin. If you would like to activate it, please contact one of your company administrators: {{listOfAdmins}}.',
  'mapSettingsInfo.FilterMenusOpen': 'Have all of the filters full expanded when loaded.',
  'mapSettingsInfo.FilterTool': 'This feature deactivates the filter tool on the presentation mode of your map.',
  'mapSettingsInfo.GroupingTool': 'This feature deactivates the grouping tool on the presentation mode of your map.',
  'mapSettingsInfo.HeatMappingTool': 'This feature deactivates the heat mapping tool on the presentation mode of your map.',
  'mapSettingsInfo.HighVolumeMarkerDisplay': 'Activating this feature will cause all individual markers to be visible and will remove all marker clustering.  This feature will improve browser performance when displaying large volumes of markers but is will also disable several marker related features.',
  'mapSettingsInfo.LabelAboveMarkersSettings': 'This feature lets you customize labels above markers.',
  'mapSettingsInfo.LabelsAboveMarkers': 'This feature will add a label above all markers on the map.',
  'mapSettingsInfo.LabelsInsideMarkers': 'This feature will add a text label inside your markers that’s generated using a column in your data set that you select.',
  'mapSettingsInfo.LassoTool': 'Turning this switch to the OFF position will deactivate the lasso tool on your shared or public maps.',
  'mapSettingsInfo.LegendReplacesGrouping': 'This will display your current groups as a legend along the side of your map in presentation mode. The grouping tool will be deactivated.',
  'mapSettingsInfo.LocationFinder': 'The location finder displays the nearest markers to an address, city, state, zip code, or your current location.',
  'mapSettingsInfo.LocationFinderStartsOpen': 'The location finder popups up when the map is loaded. This allows the map to act like a store locator.',
  'mapSettingsInfo.LocationList': 'This feature allows you to open a menu that displays a list of the address corresponding to markers on the map.',
  'mapSettingsInfo.LocationListOpen': 'This feature loads the map with the location list open.',
  'mapSettingsInfo.MapImageExport': 'Turning this feature OFF will hide it on your shared / public maps.',
  'mapSettingsInfo.MapMarkers': 'Use this feature to switch between different map marker graphics.',
  'mapSettingsInfo.MapToolsMenuOpen': 'This feature displays the tools menu open by default when your map is opened in presentation mode.',
  'mapSettingsInfo.MapZoom': 'Deactivating this feature will turn off the zoom function on your shared maps.',
  'mapSettingsInfo.MarkerLabelDefaultSettings': 'This feature lets you customize marker labels.',
  'mapSettingsInfo.MarkersVisibilityButton': 'Shows button at the bottom of the map that allows user to toggle markers visibility',
  'mapSettingsInfo.MiMkMeasurement': 'Switch between imperial and metric unit system.',
  'mapSettingsInfo.NumericLabelMarkers': 'This feature will add an ascending unique number in each marker on your map. It will also display a corresponding key down the right side of the map.',
  'mapSettingsInfo.OpenLinksInSameWindow': 'Activating this feature allows web links in the marker panel to change the current page. When off the links open in a new tab.',
  'mapSettingsInfo.RadiusProximity': 'This feature deactivates the proximity tool on the presentation mode of your map.',
  'mapSettingsInfo.RestrictMapPanning': 'When this feature is turned "ON" the map panning will be restricted to initial map bounds.',
  'mapSettingsInfo.RestrictZoomLevels': 'Restricting zoom levels prevents zooming in and zooming out.',
  'mapSettingsInfo.RoutingDirections': 'This feature deactivates the directions tool on the presentation mode of your map.',
  'mapSettingsInfo.SearchBarAlwaysVisible': 'When this feature is turned "ON" the search will always be visible your shared map.',
  'mapSettingsInfo.ShowMyLocation': 'Turning this feature OFF will hide it on your shared / presentation maps.',
  'mapSettingsInfo.ShowMyLocationDefault': 'When this feature is turned "ON" the map will automatically zoom and display their location on the map when it’s first loaded.  Note: The person viewing the map will need to agree to let Maptive find their location.',
  'mapSettingsInfo.ShowUserLocationOnAdmin': 'When this feature is turned "ON" the map will automatically zoom and display their location on the map when it’s first loaded.  Note: The person viewing the map will need to agree to let Maptive find their location.',
  'mapSettingsInfo.StackedMarkersWithPieCharts': 'Activating this feature displays a pie chart inside a marker that represents the different groups found at the same address/location or latitude and longitude.  This scenario is also known as a stacked marker.',
  'mapSettingsInfo.UnclusterBellowNMarkers': 'When the map is zoomed in enough to display less than the selected number of markers they will appear unclustered. The available limits are lower when your markers contain labels.',
  'mapSettingsInfo.UnstackMarkers': 'This feature will unstack the numbered markers that have multiple units, offices, spaces, etc at the same location.  It will produce an individual marker for each entry that will be positioned close to the others at that same location.',
  'mapSettingsInfo.enableDisableColumns': 'This feature lets you completely hide columns that you don’t want to include on a public map.',
  'mapSettingsInfo.showHideDataDisabled': 'You can’t tweak this setting in a Map View. Head over to the Main Map to make changes.',
  'mapSettingsInfo.showHideDataFromExport': 'This feature lets you hide columns of your data that you don’t want to include in data export functionalities.',
  'mapSettingsInfo.showHideDataOnMarker': 'This feature lets you hide columns of your data that you don’t want visible on the map marker panel bubbles.',
  'no data': 'no data',
  'note.existing.data.will.be.replaced': 'NOTE: The existing data set will be permanently deleted.',
  'note.include.column.headers': 'NOTE: Don’t forget to include the data column headers in your data set.',
  'of {{amountLimit}} available': 'of {{amountLimit}} available',
  'password.reset.error.unableToReset': 'We were unable to reset your password. Please try resending your password reset email and try again. If this problem persists please contact our customer support.',
  'password.reset.error.usedRecently': 'You used this password recently. Please choose a different one.',
  'password.reset.instructions': 'We have sent an email to {{email}} with a password reset link. If you will not receive any email within a few minutes please click on Resend email',
  'poi.attraction': 'Attraction',
  'poi.business': 'Business',
  'poi.government': 'Government',
  'poi.medical': 'Medical',
  'poi.park': 'Park',
  'poi.placeOfWorship': 'Place of worship',
  'poi.school': 'School',
  'poi.sportsComplex': 'Sport complex',
  'proximity.dtp.error.badParameters': 'Generation failed due to parameters. Please adjust parameters.',
  'proximity.dtp.error.limitReached': 'Drive Time Polygon limit reached. Please, contact <1>Maptive Support</1>.',
  'proximity.dtp.error.limitReached.subject': 'Drive Time Polygon Limit Reached',
  'proximity.dtp.error.unexpected': 'An unexpected error has occurred. Please modify parameters and try again',
  'proximity.dtp.error.wrongStartingPoint': 'Unable to initiate search. Ensure the start point is near a road or path.',
  'proximity.hidingGroupProximitiesBecauseTooMany': 'Your "Group" Drive Time Radii contain a significant number of circles. To maintain optimal performance, these circles will be displayed when you zoom in further.',
  'proximityExport.overCirclesLimitError': 'We currently support the export of up to {{limit}} radii. Your selection includes {{current}} radii. Please reduce the number of radii in your selection and attempt the export again.',
  'proximityExport.overTotalLocationsLimit.message': 'Hello. The export feature currently supports a maximum of {{maxItems}} locations per export, but your selection contains {{totalItemsToExport}}. Please reduce the number of proximities and/or drive time polygons selected to {{maxItems}} locations and then include the additional locations you want to export as a separate additional export. If this limitation significantly impacts your workflow, please contact our customer support team and ask for an increase on the max export limit ({{supportEmail}}).',
  'readOnly.warning.functionalityNotAvailableGeneric': 'Not Available in Read-Only mode.',
  'readOnlyMode.explanation': 'This map is in View-Only mode. You can view and interact with the data, but any changes or edits will not be saved. Some functionality is limited.',
  'readOnlyMode.name': 'View Only Mode',
  'replaceSpreadsheet.rowDuplicates.warning.caption': 'Invalid Unique Key',
  'replaceSpreadsheet.rowDuplicates.warning.message': 'The column(s) you have chosen do not identify your rows uniquely. Please Retry to select differnet column(s) or Cancel the replace process.',
  'road.arterial': 'Arterial',
  'road.highway': 'Highway',
  'road.highway.controlledAccess': 'Controlled access',
  'road.local': 'Local',
  'roads.others': 'Others',
  'sales-map.modal.success.text': 'Your map was created successfully. You can close this tab and continue generating your territories. You can also stay on this map and modify it further.',
  'salesforce.registerFinish.generalError': 'Unable to finish the registration process. Please try again or contact <email>our support</email>.',
  'salesforce.registerFinish.instructions': 'To finish your Salesforce registration, please create a password.',
  'shareNavigationLinks.failed.caption': 'Failed To Send Navigation Links Email',
  'shareNavigationLinks.instructions': 'Please select the email address to share navigation links with:',
  'spreadsheet.note.column.header': 'NOTE: Don’t forget to include descriptive headers at the top of the columns in your data set. They should be the first row in your data set.',
  'spreadsheet.note.column.header.sales-map': 'Each line of your data should represent a location of your sales people.',
  'teamShareMap.addingUserError': 'There was an error sharing the map with a user. Please try again, if this problem persists, please contact our customer support.',
  'teamShareMap.addingUserErrorTitle': 'Error sharing the map',
  'teamShareMap.changeOwner.warning': 'By making this change you will no longer be the owner of this map, and will just be an editor, do you want to continue?',
  'teamShareMap.editError': 'The operation to edit your Team has failed. Please try again, if this problem persists, please contact our customer support.',
  'teamShareMap.editErrorTitle': 'Error editing your team.',
  'teamShareMap.onlyOwnerCanShare': 'Only Map Owners can share maps with other team members.',
  'territory group': 'territory group',
  'transit.line': 'Line',
  'transit.station': 'Station',
  'transit.station.airport': 'Airport',
  'transit.station.bus': 'Bus',
  'transit.station.rail': 'Rail',
  'unclusterBelowN.noteUnstackForcedOn': 'To lessen the performance impact of this setting, {{unstackName}} will be automatically activated when you set the uncluster limit to {{threshold}} markers or more.',
  'unclusterBelowN.performanceImpactWarning': 'Enabling this feature could adversely affect the responsiveness of your map. If you encounter any performance issues, consider disabling this setting or reducing the limit number.',
  'userManagement.alert.migrationInProgress': 'Please use the <1>previous</1> version of Maptive to manage your team structure (adding, removing, or disabling users), as this functionality is temporarily unavailable in this new Beta version.',
  'userManagement.error.emailUsedAlready': 'Email already in use',
  'userManagement.error.membersLimitReached': 'Oops! You’ve hit the limit for members on your current subscription. To expand your team and upgrade to a plan with a higher member capacity, please contact <1>Maptive Support</1>.',
  'userManagement.error.membersLimitReached.noMemberAllowance': 'It looks like your current subscription doesn’t include the ability to add more members. To unlock this feature and upgrade to our Team Plan, please contact <1>Maptive Support</1>.',
  'userManagement.error.membersLimitReached.subject': 'Member Limit Reached',
  'userManagement.memberActions.allowPublicExport': 'Allow Shared/Presentational Maps Data Export',
  'userManagement.memberActions.disallowPublicExport': 'Disallow Shared/Presentational Maps Data Export',
  'welcomeModal.2FA': 'Increase your account security by turning on 2 Factor Authentication (2FA).',
  'welcomeModal.caption': 'Welcome to maptive!',
  'welcomeModal.theme': 'Select your Maptive theme style. You can choose between Light or Dark theme.',
  'welcomeModal.title': 'Before you start using Maptive, you might consider the following:',
  'welcomeModal.tutorialVideo': 'To familiarize yourself with Maptive, we invite you to watch our quick-start tutorial.',
  'welcomeModal.tutorialVideo.title': 'Quick-start tutorial',
  'zip codes': 'zip codes',
  '{{N}} Markers': '{{N}} Markers',
  '{{count}} Groups': '{{count}} Groups',
  '{{count}} Groups_one': '{{count}} Group',
  '{{count}} Item': '{{count}} Items',
  '{{count}} Item_one': '{{count}} Item',
  '{{count}} Map_one': '{{count}} Map',
  '{{count}} Map_other': '{{count}} Maps',
  '{{count}} Stops': '{{count}} Stops',
  '{{count}} Stops_one': '{{count}} Stop',
  '{{count}} clicks': '{{count}} clicks',
  '{{count}} clicks_one': '{{count}} click',
  '{{count}} element added to the map': '{{count}} elements added to the map',
  '{{count}} element added to the map_one': '{{count}} element added to the map',
  '{{count}} locations': '{{count}} locations',
  '{{count}} locations_one': '{{count}} location',
  '{{count}} sec': '{{count}} sec',
  '{{count}} sec_one': '{{count}} sec',
  '{{hours}} hours': '{{hours}} hours',
  '{{hours}} hrs': '{{hours}} hrs',
  '{{hours}} hrs_one': '{{hours}} hr',
  '{{hour}} hour': '{{hour}} hour',
  '{{hour}} hr': '{{hour}} hr',
  '{{minutes}} mins': '{{minutes}} mins',
  '{{minutes}} mins_one': '{{minutes}} mins',
  '{{minute}} min': '{{minute}} min',
  '{{n}} items here': '{{n}} items here',
  '{{percentageValue}}% Used': '{{percentageValue}}% Used',
  '{{valueFrom}} to {{valueTo}}': '{{valueFrom}} to {{valueTo}}',
  '{{value}}+': '{{value}}+',
  Accept: 'Accept',
  AccessForbiddenForCurrentRole: 'This page can be accessed only by users with a certain role. To access this page, contact your <1>Maptive account owner</1>',
  Account: 'Account',
  AccountSettingsLockInfo: 'Please use the <1>previous</1> version of Maptive to make any account settings changes, as this functionality is temporarily unavailable in the current version you are using.',
  Action: 'Action',
  Actions: 'Actions',
  Activate: 'Activate',
  Active: 'Active',
  Add: 'Add',
  Address: 'Address',
  Allowed: 'Allowed',
  And: 'And',
  Arrows: 'Arrows',
  Authorize: 'Authorize',
  Average: 'Average',
  Back: 'Back',
  BackgroundColor: 'Background Color',
  BackgroundOpacity: 'Background Opacity',
  Billed: 'Billed',
  Black: 'Black',
  BorderColor: 'Border Color',
  Boundaries: 'Boundaries',
  Boundary: 'Boundary',
  Callouts: 'Callouts',
  Canada: 'Canada',
  Cancel: 'Cancel',
  Categories: 'Categories',
  ChangesPendingMessage: 'You have unsaved Changes. Do you wish to save them?',
  Chart: 'Chart',
  Check: 'Check',
  Circles: 'Circles',
  City: 'City',
  Clear: 'Clear',
  Click: 'Click',
  Close: 'Close',
  Code: 'Code',
  Color: 'Color',
  Columns: 'Columns',
  Complete: 'Complete',
  Confirm: 'Confirm',
  Connected: 'Connected',
  Contact: 'Contact',
  Contains: 'Contains',
  Continue: 'Continue',
  Contributor: 'Contributor',
  Convert: 'Convert',
  Copy: 'Copy',
  Country: 'Country',
  Data: 'Data',
  Deactivate: 'Deactivate',
  Decline: 'Decline',
  Delete: 'Delete',
  Deleted: 'Deleted',
  Deleting: 'Deleting',
  DemographicCensusData: 'Demographic Census Data',
  Dense: 'Dense',
  Directions: 'Directions',
  Disconnect: 'Disconnect',
  Disconnected: 'Disconnected',
  Dissipate: 'Dissipate',
  Distance: 'Distance',
  Done: 'Done',
  DoughnutChart: 'Doughnut Chart',
  Drag: 'Drag',
  Draw: 'Draw',
  Drawing: 'Drawing',
  Drawings: 'Drawings',
  Edit: 'Edit',
  Editor: 'Editor',
  Email: 'Email',
  Empty: 'Empty',
  Equals: 'Equals',
  Error: 'Error',
  Exporting: 'Exporting',
  Exports: 'Exports',
  FAQ: 'FAQ',
  Failed: 'Failed',
  Filter: 'Filter',
  FontColor: 'Font Color',
  FontSize: 'Font Size',
  From: 'From',
  Full: 'Full',
  Geometry: 'Geometry',
  Go: 'Go',
  Good: 'Good',
  Gradient: 'Gradient',
  Group: 'Group',
  Grouping: 'Grouping',
  Help: 'Help',
  High: 'High',
  HorizontalBarChart: 'Horizontal Bar Chart',
  Hover: 'Hover',
  Ignore: 'Ignore',
  Images: 'Images',
  Important: 'Important',
  Labels: 'Labels',
  Landmarks: 'Landmarks',
  Lat: 'Lat',
  Latitude: 'Latitude',
  LayeredMapMatchupTip: '<p>You’re almost there! Simply match the corresponding columns and then decide on a name for your layered column. You can either use one of the original column names or come up with a brand-new one.</p> <p>The <faListTree /> button removes a column you added yourself, or breaks apart a matched column into separate columns for each source map. The <faCircleXmark /> button controls whether the column will be included (gray) or excluded (red) in the resulting layered map. Columns can also be left empty and filled in later.</p>',
  Legend: 'Legend',
  Light: 'Light',
  Lng: 'Lng',
  Locations: 'Locations',
  Longitude: 'Longitude',
  Low: 'Low',
  MapMigrationClearSelection: 'Clear Selection',
  MapMigrationCompleteFailHeading: 'Complete migration failure',
  MapMigrationCompleteFailInfo: 'To rectify this problem, please re-upload your data onto the corresponding maps in v4 and then restart the migration process. This should lead to the successful migration of your data.',
  MapMigrationCompleteFailNoPartialsWarning: 'For some maps, the migration process failed. We couldn’t transfer any data from the following maps from v4 to v5:',
  MapMigrationCompleteFailWarning: 'For some maps, the migration process failed completely. We couldn’t transfer any data from the following maps from v4 to v5:',
  MapMigrationConfirmation: 'You selected maps that have already been migrated previously. If you proceed you will override any changes you’ve made to the previously imported maps.',
  MapMigrationConfirmationHeading: 'Confirm map migration',
  MapMigrationConfirmationInstructions: 'In order to confirm overwriting other people’s maps type "{{phrase}}" into the following input box.',
  MapMigrationConfirmationPhrase: 'I agree',
  MapMigrationDevErrorInfoInstructions: 'If you keep having issues with migrating a map, provide the following information to our support:',
  MapMigrationDialogInstructions: 'Choose the maps you want to import. They’ll arrive with all their data intact.',
  MapMigrationFailedIntro: 'During the migration process, we encountered issues with some of your maps.',
  MapMigrationForceSelectionExplanation: 'This map cannot be deselected. It is selected because it is a dependency of another selected map.',
  MapMigrationLastMigratedColumnName: 'Last Migrated',
  MapMigrationMapsMigrated: 'Maps Migrated:',
  MapMigrationMigrateAllMaps: 'Migrate All Maps',
  MapMigrationMigrateMapsCaption: 'Migrate Maps',
  MapMigrationMigrateNewMaps: 'Select New Maps',
  MapMigrationMigrateNewMapsDisabled: 'All your maps have already been migrated. Select maps you’d like to migrate again.',
  MapMigrationMigrateNewMapsExplanation: 'This will select the maps, that have not yet been migrated.',
  MapMigrationMigrateSelectedMaps: 'Migrate {{count}} Selected Maps',
  MapMigrationMigrateSelectedMaps_one: 'Migrate the Selected Map',
  MapMigrationMigrateSelectedMaps_zero: 'Migrate Selected Maps',
  MapMigrationNotOwnedByYouWarning: 'This map is owned by {{mapOwner}}. Migrating it will overwrite their data.',
  MapMigrationOtherUsersMapsConfirmation: 'You have selected maps of other people for migration. Proceeding with the migration will irreversibly overwrite their maps.',
  MapMigrationPartialFailHeading: 'Partial migration failure',
  MapMigrationPartialFailInfo: 'We recommend you to inspect the affected maps to ensure all your data is present and accurate. If you find discrepancies or missing information, you can address these issues by re-uploading your data onto the corresponding maps in v4. After re-uploading, you can re-initiate the migration process.',
  MapMigrationPartialFailWarning: 'During the migration process, we were able to recover only part of the data from some spreadsheets. The following maps have data discrepancy issues, meaning not all the original data in v4 was successfully migrated to v5:',
  MapMigrationProgressExplanation: 'Given the volume of data being moved, this operation may take some time. We kindly ask for your patience. Please note, the map-related features will not be accessible during this time. Thank you for your understanding.',
  MapMigrationReImportWarning: 'If a previously imported map is imported again, it will be updated with the data from the original version. This means any changes you have made to this map in the new app will be lost. Handle reimports cautiously to avoid unintentional data loss.',
  MapMigrationWelcomeToBetaCaption: 'Welcome to BETA!',
  MapTemporaryUnavailable: 'This map is temporarily unavailable, please contact the map owner or <1>Maptive Support</1> to resolve the issue.',
  Maps: 'Maps',
  MapsDeletionConfirmation: 'Are you sure you want to delete the selected Map(s) or Saved Map View(s)? You can use the "Undo" feature to reload any deleted map for 30 days. After that, the deleted map will be permanently removed from your account.',
  Maptive: 'Maptive',
  Marker: 'Marker',
  MarkerCountLocationDensity: 'Marker Count / Location Density',
  Markers: 'Markers',
  Max: 'Max',
  Median: 'Median',
  Medium: 'Medium',
  Metric: 'Metric',
  Min: 'Min',
  MyGroupTerritoryData: 'My Group / Territory Data',
  MyNumericalData: 'My Numerical Data',
  Name: 'Name',
  Navigate: 'Navigate',
  NavigateToApple: 'Navigate To (w/ Apple)',
  NavigateToGoogle: 'Navigate To (w/ Google)',
  NoFill: 'No Fill',
  None: 'None',
  Notice: 'Notice',
  Numbers: 'Numbers',
  Numeric: 'Numeric',
  Numerical: 'Numerical',
  OK: 'OK',
  Off: 'Off',
  Offline: 'Offline',
  On: 'On',
  Opacity: 'Opacity',
  Open: 'Open',
  Options: 'Options',
  Or: 'Or',
  Owner: 'Owner',
  PaddingHorizontal: 'Padding on Sides',
  PaddingVertical: 'Padding Top and Bottom',
  Partial: 'Partial',
  Password: 'Password',
  Pause: 'Pause',
  Pending: 'Pending',
  PieChart: 'Pie Chart',
  Pixels: 'Pixels',
  Places: 'Places',
  PolarAreaChart: 'Polar Area Chart',
  Polygons: 'Polygons',
  Polylines: 'Polylines',
  Poor: 'Poor',
  Print: 'Print',
  Private: 'Private',
  Proceed: 'Proceed',
  Public: 'Public',
  RadarChart: 'Radar Chart',
  Radius: 'Radius',
  Reconnect: 'Reconnect',
  Rectangles: 'Rectangles',
  Refresh: 'Refresh',
  Reject: 'Reject',
  Reset: 'Reset',
  Restore: 'Restore',
  Resume: 'Resume',
  Retry: 'Retry',
  Roads: 'Roads',
  Routing: 'Routing',
  Sample: 'Sample',
  Save: 'Save',
  Saved: 'Saved',
  Saving: 'Saving',
  ScatterChart: 'Scatter Chart',
  Search: 'Search',
  Select: 'Select',
  Selection: 'Selection',
  Send: 'Send',
  Settings: 'Settings',
  Share: 'Share',
  Snapshots: 'Snapshots',
  Sort: 'Sort',
  Start: 'Start',
  Stay: 'Stay',
  Step: 'Step',
  Stop: 'Stop',
  Strong: 'Strong',
  Submit: 'Submit',
  Sum: 'Sum',
  Sync: 'Sync',
  Syncing: 'Syncing',
  Territories: 'Territories',
  Territory: 'Territory',
  Text: 'Text',
  Texts: 'Texts',
  Threshold: 'Threshold',
  To: 'To',
  Tools: 'Tools',
  Total: 'Total',
  Trial: 'Trial',
  Tutorials: 'Tutorials',
  Type: 'Type',
  Undo: 'Undo',
  Unknown: 'Unknown',
  Unmatched: 'Unmatched',
  Upgrade: 'Upgrade',
  Value: 'Value',
  Verify: 'Verify',
  VerticalBarChart: 'Vertical Bar Chart',
  ViewFromToOf: 'View {{from}} - {{to}} of {{total}}',
  Views: 'Views',
  Warning: 'Warning',
  Weak: 'Weak',
  White: 'White',
  Zoom: 'Zoom',
  [CreateMapErrorCode.NoHeaderColumnNames]: 'The first row of your data set must contain column headers. Please add them and try again.',
  [CustomizationLevel.All]: 'All Markers',
  [CustomizationLevel.Group]: 'Only Markers In This Group',
  [CustomizationLevel.Individual]: 'Just This Specific Marker',
  [DataType.ATTRIBUTE]: 'Attribute',
  [DataType.DATE]: 'Date',
  [DataType.GROUP]: 'Group',
  [DataType.NUMBER]: 'Number',
  [DataType.TEXT]: 'Text',
  [DrawingTool.Arrow]: 'Arrow',
  [DrawingTool.Callout]: 'Callout',
  [DrawingTool.Circle]: 'Circle',
  [DrawingTool.IconMarker]: 'Icon Marker',
  [DrawingTool.Image]: 'Image',
  [DrawingTool.Label]: 'Label',
  [DrawingTool.Number]: 'Number',
  [DrawingTool.Polygon]: 'Polygon',
  [DrawingTool.Polyline]: 'Polyline',
  [DrawingTool.Rectangle]: 'Rectangle',
  [DrawingTool.Text]: 'Text',
  [EmailErrorCode.EMAIL_ALREADY_TAKEN]: 'This e-mail address is already taken.',
  [EmailErrorCode.EMAIL_BLOCKED]: 'This email address was blocked for security reasons.',
  [EmailErrorCode.UNVERIFIED_EMAIL]: 'This email address hasn’t been verified yet.',
  [FileUploadErrorCodes.FAILED]: 'File upload failed.',
  [FileUploadErrorCodes.FILE_READING_ERROR]: 'The file format is not valid.',
  [GeneralErrorCode.ACCESS_FORBIDDEN]: 'User is already signed in',
  [GeneralErrorCode.FORBIDDEN]: 'Something went wrong. Please make sure your licence is active.',
  [GeneralErrorCode.IP_BLOCKED]: 'This IP address was blocked for security reasons.',
  [GeneralErrorCode.SERVER_ERROR]: 'We are experiencing some issues, please try again later.',
  [GeneralErrorCode.UNKNOWN_ERROR]: 'We are experiencing some issues, please try again later.',
  [LottieActiveMarkerIndicators.lottieCaretDownShort]: 'Caret Down',
  [LottieActiveMarkerIndicators.lottieChevron]: 'Chevron Arrow',
  [LottieActiveMarkerIndicators.lottieDoubleDownArrow]: 'Double Arrow',
  [LottieActiveMarkerIndicators.lottieScrollDownArrowChain]: 'Arrow Chain',
  [MFAErrorCode.MFA_CODE_IS_INVALID]: 'Two-factor code is invalid.',
  [MFAErrorCode.MFA_CODE_IS_REQUIRED]: 'A 2FA code is required.',
  [MFAErrorCode.MFA_CODE_SENT_TO_EMAIL]: 'Two-factor code was sent to your email.',
  [MFAErrorCode.MFA_CODE_SENT_TO_PHONE]: 'Two-factor code was sent to your phone number.',
  [MFAErrorCode.MFA_REQUIRED]: 'A 2FA code is required.',
  [MapSearchFilterBy.OnlyGeolocated]: 'Google Location Matches Only',
  [MapSearchFilterBy.OnlyUserData]: 'Search My Data Only',
  [MapSearchFilterBy.UserDataAndGeolocated]: 'Search My Data & Google Location Matches',
  [PageTitle.CONGRATULATIONS]: 'Congratulations!',
  [PageTitle.DATA]: 'Data',
  [PageTitle.HOME]: 'Home',
  [PageTitle.LOGIN]: 'Login',
  [PageTitle.MAP]: 'Map',
  [PageTitle.PRESENTATIONAL]: '{{mapName}}',
  [PageTitle.SUBSCRIBE]: 'Subscribe',
  [PasswordErrorCode.OLD_PASSWORD_INVALID]: 'Invalid password. Please check and try again.',
  [PasswordErrorCode.PASSWORD_CONFIRMATION_FAILED]: 'Password confirmation doesn’t match.',
  [PasswordErrorCode.PASSWORD_HISTORY_REUSE]: 'This password has been used before. Please use a new password.',
  [PasswordErrorCode.PASSWORD_REQUIRES_LETTER]: 'Password must include at least one letter.',
  [PasswordErrorCode.PASSWORD_REQUIRES_MIXED_CASE]: 'Password must include a mix of both uppercase and lowercase letters.',
  [PasswordErrorCode.PASSWORD_REQUIRES_NUMBERS]: 'Password must include at least one number.',
  [PasswordErrorCode.PASSWORD_REQUIRES_SYMBOLS]: 'Password must include at least one special symbol.',
  [RefreshTokenErrorCodes.INVALID_TOKEN]: 'You have been signed out due to logging in from a different device',
  [RestoreOption.AllIndividual]: 'All Individual Customized Markers',
  [RestoreOption.All]: 'All Markers',
  [RestoreOption.Group]: 'Only Markers In This Group',
  [RestoreOption.Individual]: 'This Specific Marker Only',
  [SpreadsheetImportErrorCodes.DIFFERENT_STRUCTURE]: 'The column headers in your imported data do not align with the existing spreadsheet structure. Please ensure all column names match the current spreadsheet format before attempting to import again.',
  [SpreadsheetImportErrorCodes.FAILED_TO_PREPARE_SOURCE]: 'We encountered an issue while preparing your data for import. Please check the file format and try again. If the problem persists, contact our support team for assistance.',
  [SpreadsheetImportErrorCodes.FAILED_TO_PREPARE_STRUCTURE]: 'We encountered an issue while preparing the header of your data for import. Please check the header of your data and try again. If the problem persists, contact our support team for assistance.',
  [SvgActiveMarkerIndicators.basicCircleArrow]: 'Basic Circle',
  [SvgActiveMarkerIndicators.chevronArrow]: 'Chevron Down Arrow',
  [SvgActiveMarkerIndicators.fatCircleArrow]: 'Fat Circle Arrow',
  [SvgActiveMarkerIndicators.neonArrow]: 'Border Arrow',
  [SvgActiveMarkerIndicators.radarCircle]: 'Radar Circle',
  [SvgActiveMarkerIndicators.simpleArrow]: 'Simple Arrow',
  [SvgActiveMarkerIndicators.slimCircleArrow]: 'Slim Circle Arrow',
  [SvgActiveMarkerIndicators.tripleNeonArrow]: 'Triple Border Arrow',
  [UserLoginErrorSpecialCode.INVALID_CREDENTIALS]: 'Provided email or password is invalid.',
  [UserLoginErrorSpecialCode.USER_CLIENT_IS_DISABLED]: 'Account Disabled or Deleted. If you need assistance or believe this is an error, contact our <1>support team</1>.',
  [UserLoginErrorSpecialCode.USER_NOT_FOUND]: 'Email Address Not Found. Please check the email you entered. It might not have been used to create an account or the account has been deleted.',
  [UserLoginErrorSpecialCode.USER_WAS_DISABLED]: 'Your account was deactivated by your team Admin. Please contact the Admin of your Maptive account if you have any questions',
  [ValueValidationErrorCode.FIELD_MUST_BE_A_STRING]: 'Value must be alphanumeric characters',
  [ValueValidationErrorCode.FIELD_MUST_BE_A_VALID_EMAIL_ADDRESS]: 'Value must be a valid e-mail address.',
  [ValueValidationErrorCode.MAX_LENGTH_EXCEEDED]: 'Value exceeds maximum length of {{length}}.',
  [ValueValidationErrorCode.MIN_LENGTH_REQUIRED]: 'Value is less than minimum length of {{length}}.',
  [ValueValidationErrorCode.PROVIDED_VALUE_IS_INVALID]: 'Provided value is not one of the allowed options.',
  [ValueValidationErrorCode.VALUE_SIZE_IS_INVALID]: 'Provided value does not match the required length.',
  administrative: 'Administrative',
  all: 'All',
  artificialAreaCode: 'Fill Area {{areaCode}}',
  boundary: 'boundary',
  bulkEditExplanation: 'Check the <strong>“{{batchEdit}}”</strong> boxes on the right ONLY for the categories you want to batch edit to the same value / text. Then, add the new value / text in the corresponding category input field. <Unchanging>All blank and unchecked category boxes will keep their existing values / text as is.</Unchanging>',
  calendarMonth0: 'January',
  calendarMonth10: 'November',
  calendarMonth11: 'December',
  calendarMonth1: 'February',
  calendarMonth2: 'March',
  calendarMonth3: 'April',
  calendarMonth4: 'May',
  calendarMonth5: 'June',
  calendarMonth6: 'July',
  calendarMonth7: 'August',
  calendarMonth8: 'September',
  calendarMonth9: 'October',
  color: 'Color',
  dark: 'Dark',
  drawn: 'drawn',
  emptyRowsNotCounted: '{{emptyRowCount}} of {{rowCount}} rows not counted',
  filterSettingsOnOffNote: '*Turning the filter tool "off" for any of your data will remove/hide it from the filter tool. It will still be visible on the map.',
  geometry: 'Geometry',
  go: 'Go',
  here: 'here',
  hidden: 'hidden',
  hrs: 'hrs',
  inherit: 'inherit',
  km: 'km',
  label: 'label',
  labels: 'Labels',
  landscape: 'Landscape',
  lassoActiveClickOnMapInstructions: 'Lasso active, start by clicking on the map.',
  light: 'Light',
  lightness: 'Lightness',
  locationsMappedStatisticsExplanation: '<p>The number you’re seeing represents the unique locations you’ve added to your map. Please note, the following aren’t included in this total:</p><ul><li>Any rows that already contained latitude and longitude coordinates</li><li>Locations you’ve previously mapped</li></ul>',
  mapDirectionsModalBeginAtUserLocationDescription: 'Automatically use your current location (via the GPS on your phone or the IP location of your computer) as the first marker in any route or directions of locations.',
  mapDirectionsModalRouteDraggingDescription: 'Allows you to customize routes created by Google by dragging and dropping sections that you wish to modify.',
  matchUpModalInfoMessage: 'We just auto matched your data! Please match up or correct any columns in your data that we missed.',
  max: 'max',
  mi: 'mi',
  min: 'min',
  mondayToFriday: 'Monday - Friday',
  notNumericalColumnWarning: 'The action you selected requires a numerical column, but the column you chose does not contain numerical data. Please select a column with numerical data to perform this action.',
  note: 'note',
  ofATotalOf: 'of',
  optional: 'optional',
  or: 'or',
  other: 'Other',
  poi: 'Points of interest',
  quickStartLink: 'Maptive 5 Quickstart Guide',
  resetToVersion: 'Reset to Version',
  road: 'Road',
  saturation: 'Saturation',
  selected: 'selected',
  shouldMatchExactWords: 'Only match on exact word matches. Partial word matches ignored.',
  shouldZoomOnSearchMatches: 'Zoom in on Map to Search Matches',
  shown: 'shown',
  simplified: 'simplified',
  states: 'states',
  to: 'to',
  toNumber: 'to',
  transit: 'Transit',
  undoMapDeletion: 'Undo Map Deletion',
  undoSpecificAction: 'Undo Specific Action',
  unlimited: 'Unlimited',
  used: 'Used',
  visibility: 'Visibility',
  water: 'Water',
  weight: 'Weight',
// End of translation lines
};

// eslint-disable-next-line import/no-default-export
export default translations;
